import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import axios from 'axios'
import { toast } from 'react-toastify'
import { PulseLoader } from 'react-spinners'
import { createPortal } from 'react-dom'
import { normalizePhoneNumber } from '../utils/phoneNumber'
import {
  extractPdfFieldsFromResponse,
  hasSalesQuotePdf,
  resolveSqPdfDownloadUrl
} from '../utils/sqPdfDownload'

const API_URL = process.env.REACT_APP_API_URL

const CURRENCY_OPTIONS = ['INR', 'Dollar', 'Euro']
const DEFAULT_QUOTE_STATUS = 'open'
const FINAL_STATUS = 'Active'

const emptyMaterial = () => ({
  materialCode: '',
  materialDescription: '',
  hsn: '',
  uom: '',
  quantity: '',
  prNumber: '',
  pr_id: '',
  clientReference: '',
  technicalRemarks: '',
  basicPrice: '',
  gst: '18',
  totalValue: '',
  quotePrice: ''
})

const emptyTerm = () => ({
  quoteTerm: '',
  quoteResponse: ''
})

const DEFAULT_QUOTE_TERMS = [
  {
    quoteTerm: 'Payment Terms',
    quoteResponse: '30 days from the receipt of material'
  },
  {
    quoteTerm: 'Freight Terms',
    quoteResponse: 'Extra - at actulas'
  },
  {
    quoteTerm: 'Delivery Location',
    quoteResponse: 'Client Stores'
  },
  {
    quoteTerm: 'Lead Times',
    quoteResponse: ''
  },
  {
    quoteTerm: 'Warranty Terms',
    quoteResponse: ''
  },
  {
    quoteTerm: 'Special Instructions',
    quoteResponse: ''
  }
]

const MANDATORY_QUOTE_TERM_NAMES = [
  'Lead Times',
  'Warranty Terms',
  'Special Instructions'
]

function isMandatoryQuoteTerm(quoteTerm) {
  const name = String(quoteTerm || '').trim().toLowerCase()
  return MANDATORY_QUOTE_TERM_NAMES.some(
    (term) => term.toLowerCase() === name
  )
}

function getTermFieldErrorKey(quoteTerm) {
  return `term_${String(quoteTerm || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_')}`
}

function parseAdditionalCcEmails(value) {
  return String(value || '')
    .split(/[,;]+/)
    .map((email) => email.trim())
    .filter(Boolean)
}

function isValidEmailAddress(value) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || '').trim())
}

function buildCcEmails(userEmail, additionalInput) {
  const result = []
  const seen = new Set()
  const addEmail = (email) => {
    const trimmed = String(email || '').trim()
    if (!trimmed) return
    const key = trimmed.toLowerCase()
    if (seen.has(key)) return
    seen.add(key)
    result.push(trimmed)
  }

  addEmail(userEmail)
  parseAdditionalCcEmails(additionalInput).forEach(addEmail)
  return result
}

const getDefaultTerms = () =>
  DEFAULT_QUOTE_TERMS.map((term) => ({
    quoteTerm: term.quoteTerm,
    quoteResponse: term.quoteResponse
  }))

const emptyCustomer = () => ({
  customerId: '',
  customerName: '',
  customerMailId: '',
  phoneNumber: '',
  emailLink: ''
})

function customerAlreadyEmailed(customer) {
  return Boolean(String(customer?.emailLink || '').trim())
}

function flattenCustomersToContactRows(customers) {
  const rows = []
  if (!Array.isArray(customers)) return rows

  customers.forEach((customer) => {
    const customerName = String(customer.customerName || '').trim()
    const contacts = Array.isArray(customer.contacts) ? customer.contacts : []

    if (contacts.length === 0) {
      const email = String(customer.email || '').trim()
      if (email) {
        rows.push({
          customerId: customer._id || customer.id || '',
          contactId: '',
          customerName,
          contactPerson: '',
          designation: '',
          email,
          contactNumber: String(customer.contactNumber || customer.phonenumber || '').trim()
        })
      }
      return
    }

    contacts.forEach((contact) => {
      const email = String(contact.email || '').trim()
      if (!email) return
      const status = String(contact.status || 'active').toLowerCase()
      if (status === 'inactive') return

      rows.push({
        customerId: customer._id || customer.id || '',
        contactId: contact._id || contact.id || '',
        customerName,
        contactPerson: String(contact.contactPerson || '').trim(),
        designation: String(contact.designation || '').trim(),
        email,
        contactNumber: String(contact.contactNumber || contact.phoneNumber || '').trim()
      })
    })
  })

  return rows
}

function parseApiResponse(raw) {
  let d = raw
  if (typeof d === 'string') {
    try {
      d = JSON.parse(d)
    } catch {
      return raw
    }
  }
  if (d?.body != null) {
    if (typeof d.body === 'string') {
      try {
        return JSON.parse(d.body)
      } catch {
        return d.body
      }
    }
    return d.body
  }
  return d
}

function isApiSuccess(responseData) {
  if (!responseData || typeof responseData !== 'object') return false
  const s = responseData.success
  return s === true || s === 'true' || s === 1 || s === '1'
}

function mapSqOwnerFromApi(item) {
  return {
    s3Key: item?.s3Key || '',
    originalFileName: item?.originalFileName || '',
    contentType: item?.contentType || '',
    uploadedAt: item?.uploadedAt || '',
    url: item?.url || ''
  }
}

function formatFileSize(bytes) {
  if (bytes == null || Number.isNaN(Number(bytes))) return ''
  const size = Number(bytes)
  if (size < 1024) return `${size} B`
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`
  return `${(size / (1024 * 1024)).toFixed(1)} MB`
}

function getSqOwnerListFromQuote(quote) {
  if (!quote || typeof quote !== 'object') return []
  if (Array.isArray(quote.sqOwner) && quote.sqOwner.length > 0) return quote.sqOwner
  if (Array.isArray(quote.quoteOwner) && quote.quoteOwner.length > 0) return quote.quoteOwner
  if (Array.isArray(quote.attachments) && quote.attachments.length > 0) return quote.attachments
  return []
}

const MAX_SQ_OWNER_ATTACHMENTS = 10

function getTodayIsoDate() {
  const now = new Date()
  const pad = (n) => String(n).padStart(2, '0')
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`
}

function formatDateForApi(isoDate) {
  if (!isoDate) return ''
  const [yyyy, mm, dd] = isoDate.split('-')
  if (!yyyy || !mm || !dd) return ''
  return `${dd}-${mm}-${yyyy}`
}

function getCurrentTimeHHmm() {
  const now = new Date()
  const pad = (n) => String(n).padStart(2, '0')
  return `${pad(now.getHours())}:${pad(now.getMinutes())}`
}

function formatDateTimeForApi(isoDate, timeHHmm) {
  if (!isoDate) return ''
  const [yyyy, mm, dd] = isoDate.split('-')
  if (!yyyy || !mm || !dd) return ''
  const time = String(timeHHmm || getCurrentTimeHHmm()).trim().slice(0, 5)
  return `${dd}-${mm}-${yyyy} ${time}`
}

function parseTime24(hhmm) {
  if (!hhmm) return { hour: '', minute: '', period: '' }
  const [hStr, mStr] = String(hhmm).trim().split(':')
  let h = parseInt(hStr, 10)
  const minute = String(mStr || '00')
    .padStart(2, '0')
    .slice(0, 2)
  if (Number.isNaN(h)) return { hour: '', minute: '', period: '' }

  const period = h >= 12 ? 'PM' : 'AM'
  if (h === 0) h = 12
  else if (h > 12) h -= 12

  return { hour: String(h), minute, period }
}

function formatTime12Preview(hhmm) {
  const { hour, minute, period } = parseTime24(hhmm)
  if (!hour || !minute || !period) return ''
  return `${hour}:${minute} ${period}`
}

function getDeliveryDateTimeValue(quoteDeliveryDate, timeHHmm) {
  if (!quoteDeliveryDate) return null
  const due = new Date(`${quoteDeliveryDate}T${timeHHmm || getCurrentTimeHHmm()}`)
  return Number.isNaN(due.getTime()) ? null : due
}

function isDeliveryBeforeQuoteDate(quoteDate, quoteDeliveryDate) {
  if (!quoteDate || !quoteDeliveryDate) return false
  const delivery = getDeliveryDateTimeValue(quoteDeliveryDate, getCurrentTimeHHmm())
  const quoteStart = new Date(`${quoteDate}T00:00`)
  if (!delivery || Number.isNaN(quoteStart.getTime())) return false
  return delivery < quoteStart
}

function formatDeliveryDateTimePreview(isoDate, timeHHmm) {
  if (!isoDate) return '-'
  const datePart = formatDateForApi(isoDate)
  const timePart = formatTime12Preview(timeHHmm)
  if (!datePart) return '-'
  return timePart ? `${datePart} ${timePart}` : datePart
}

function extractCreatedQuoteId(responseData) {
  const data = responseData?.data
  if (!data) return responseData?.id || responseData?._id || null
  if (typeof data === 'string') return data
  return data._id || data.id || null
}

function parseDateFromApi(value) {
  if (!value) return ''
  const str = String(value).trim()
  if (/^\d{2}-\d{2}-\d{4}$/.test(str)) {
    const [dd, mm, yyyy] = str.split('-')
    return `${yyyy}-${mm}-${dd}`
  }
  const dateTimeMatch = str.match(/^(\d{2})-(\d{2})-(\d{4})[ T]/)
  if (dateTimeMatch) {
    const [, dd, mm, yyyy] = dateTimeMatch
    return `${yyyy}-${mm}-${dd}`
  }
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return ''
  return d.toISOString().slice(0, 10)
}

function pickQuoteFromResponse(responseData, id) {
  let dataBlock = responseData?.data != null ? responseData.data : responseData
  if (dataBlock == null) return null
  if (typeof dataBlock === 'string') {
    try {
      dataBlock = JSON.parse(dataBlock)
    } catch {
      return null
    }
  }
  if (!dataBlock || typeof dataBlock !== 'object') return null

  if (
    !Array.isArray(dataBlock) &&
    dataBlock.quotes == null &&
    (dataBlock._id != null || dataBlock.quoteNumber != null)
  ) {
    return dataBlock
  }

  const list = dataBlock.quotes
  if (!Array.isArray(list) || list.length === 0) return null
  const found = list.find((q) => String(q._id || q.id) === String(id))
  return found || list[0]
}

const getProcurementKey = (item, index) =>
  item._id ||
  `${item.prNo || ''}|${item.materialCode || ''}|${item.materialDescription || ''}|${index}`

function getProcurementQuotationStatus(item) {
  return String(item?.quotationStatus || '').trim()
}

const isEmptyMaterialRow = (row) =>
  !String(row.materialCode || '').trim() &&
  !String(row.materialDescription || '').trim() &&
  !String(row.hsn || '').trim() &&
  !String(row.uom || '').trim() &&
  !String(row.quantity || '').trim() &&
  !String(row.basicPrice || '').trim() &&
  !String(row.quotePrice || '').trim()

function isDecimalInput(value) {
  return value === '' || /^\d*\.?\d*$/.test(value)
}

function formatMaterialAmount(value) {
  const num = Number(value)
  if (!Number.isFinite(num)) return '0.00'
  return num.toFixed(2)
}

function calcMaterialPricing(basicPrice, quantity, gstPercent) {
  const basic = parseFloat(basicPrice) || 0
  const qty = parseFloat(quantity) || 0
  const gst = parseFloat(gstPercent) || 0
  const lineBasic = basic * qty
  const gstValue = (lineBasic * gst) / 100
  const totalValue = lineBasic + gstValue
  return { lineBasic, gstValue, totalValue }
}

function calcMaterialLineTotal(basicPrice, quantity, gstPercent) {
  const { lineBasic, totalValue } = calcMaterialPricing(basicPrice, quantity, gstPercent)
  if (lineBasic <= 0) return ''
  return totalValue.toFixed(2)
}

function calcGrossTotalsFromRows(rows) {
  return rows.reduce(
    (acc, row) => {
      const { lineBasic, gstValue, totalValue } = calcMaterialPricing(
        row.basicPrice,
        row.quantity,
        row.gst
      )
      return {
        grossTotalBasicPrice: acc.grossTotalBasicPrice + lineBasic,
        grossGstValue: acc.grossGstValue + gstValue,
        grossTotalValue: acc.grossTotalValue + totalValue
      }
    },
    { grossTotalBasicPrice: 0, grossGstValue: 0, grossTotalValue: 0 }
  )
}

function formatTechnicalRemarksFromApi(record) {
  return String(
    record?.technicalRemarks ??
      record?.technicalQueries ??
      record?.technicalResponse ??
      ''
  ).trim()
}

function mapMaterialFromApi(m) {
   const basicPrice =
    m.basicPrice != null && m.basicPrice !== '' && Number(m.basicPrice) !== 0
      ? String(m.basicPrice)
      : ''
  const quantity = m.quantity != null && m.quantity !== '' ? String(m.quantity) : ''
  const gst =
    m.gst != null && m.gst !== ''
      ? String(m.gst)
      : m.gstPercent != null && m.gstPercent !== ''
        ? String(m.gstPercent)
        : '18'
  const totalFromApi =
    m.totalValue != null && m.totalValue !== '' ? String(m.totalValue) : ''
  const totalValue =
    calcMaterialLineTotal(basicPrice, quantity, gst) || totalFromApi

  return {
    sno: m.sno != null && m.sno !== '' ? m.sno : '',
    materialId: m._id || m.materialId || m.id || '',
    materialCode: m.materialCode || '',
    materialDescription: m.materialDescription || '',
    hsn: m.hsn || m.hsnCode || '',
    uom: m.uom || '',
    quantity,
    prNumber: m.prNumber || m.prNo || '',
    pr_id: m.pr_id || m.prId || '',
    clientReference: m.clientReference || '',
    technicalRemarks: formatTechnicalRemarksFromApi(m),
    basicPrice,
    gst,
    totalValue,
    quotePrice:
      m.quotePrice != null && m.quotePrice !== '' ? String(m.quotePrice) : ''
  }
}

function materialMatchesProcurement(material, procurement) {
  if (!material || isEmptyMaterialRow(material)) return false

  const matPrId = String(material.pr_id || '').trim()
  const procId = String(
    procurement._id || procurement.pr_id || procurement.id || ''
  ).trim()
  if (matPrId && procId && matPrId === procId) return true

  const matPr = String(material.prNumber || '').trim()
  const procPr = String(procurement.prNo || procurement.prNumber || '').trim()
  const matCode = String(material.materialCode || '').trim()
  const procCode = String(procurement.materialCode || '').trim()
  const matDesc = String(material.materialDescription || '').trim()
  const procDesc = String(procurement.materialDescription || '').trim()

  if (!matCode && !matDesc && !matPr) return false

  return matPr === procPr && matCode === procCode && matDesc === procDesc
}

function isProcurementInMaterials(materials, procurement) {
  return materials.some((material) => materialMatchesProcurement(material, procurement))
}

const findNextMaterialWriteIndex = (rows) => {
  const emptyIndex = rows.findIndex(isEmptyMaterialRow)
  return emptyIndex === -1 ? rows.length : emptyIndex
}

const SalesQuotesAdd = () => {
  const navigate = useNavigate()
  const { id: editId } = useParams()
  const isEditMode = Boolean(editId)
  const [activeTab, setActiveTab] = useState('details')
  const [isLoading, setIsLoading] = useState(false)
  const [isFetchingQuoteNumber, setIsFetchingQuoteNumber] = useState(!editId)
  const [isLoadingQuote, setIsLoadingQuote] = useState(Boolean(editId))
  const [quoteId, setQuoteId] = useState(editId || null)
  const [quoteRecordStatus, setQuoteRecordStatus] = useState('')
  const [fieldErrors, setFieldErrors] = useState({})

  const [quoteDetails, setQuoteDetails] = useState({
    quoteNumber: '',
    quoteDate: getTodayIsoDate(),
    quoteDeliveryDate: '',
    quoteSummary: '',
    quoteCurrency: 'INR',
    pdfLink: '',
    pdfDownloadUrl: '',
    quoteDeliveryTimeDisplay: ''
  })

  const [terms, setTerms] = useState(getDefaultTerms)
  const [materials, setMaterials] = useState([emptyMaterial()])
  const [activeMaterialRowIndex, setActiveMaterialRowIndex] = useState(0)
  const [prNoSearch, setPrNoSearch] = useState('')
  const [prSearchLoading, setPrSearchLoading] = useState(false)
  const [prSearchResults, setPrSearchResults] = useState([])
  const [selectedPrKeys, setSelectedPrKeys] = useState(new Set())
  const [prSearchResultsOpen, setPrSearchResultsOpen] = useState(false)

  const [customerSearch, setCustomerSearch] = useState('')
  const [customerSearchLoading, setCustomerSearchLoading] = useState(false)
  const [customerSearchResults, setCustomerSearchResults] = useState([])
  const [customers, setCustomers] = useState([emptyCustomer()])
  const prSearchDebounceRef = useRef(null)
  const prSearchRequestIdRef = useRef(0)
  const customerSearchDebounceRef = useRef(null)
  const customerSearchRequestIdRef = useRef(0)

  const [showPreviewStep, setShowPreviewStep] = useState(false)
  const [isSendingEmail, setIsSendingEmail] = useState(false)
  const [isMailSent, setIsMailSent] = useState(false)
  const [userEmail, setUserEmail] = useState('')
  const [additionalCcEmails, setAdditionalCcEmails] = useState('')
  const [ccUserSuggestions, setCcUserSuggestions] = useState([])
  const [isSearchingCcUsers, setIsSearchingCcUsers] = useState(false)
  const ccSearchDebounceRef = useRef(null)
  const ccSearchRequestIdRef = useRef(0)
  const [customerEmailSuggestIndex, setCustomerEmailSuggestIndex] = useState(null)
  const [customerEmailSuggestions, setCustomerEmailSuggestions] = useState([])
  const [isSearchingCustomerEmails, setIsSearchingCustomerEmails] = useState(false)
  const customerEmailDebounceRef = useRef(null)
  const materialDescInputRefs = useRef({})                 
  const [showMaterialDescDropdown, setShowMaterialDescDropdown] = useState({})    
  const [selectedMaterialDescIndex, setSelectedMaterialDescIndex] = useState({})  
  const [materialDescDropdownPos, setMaterialDescDropdownPos] = useState({})     
  const customerEmailRequestIdRef = useRef(0)

  useEffect(() => {
    return () => {
      if (ccSearchDebounceRef.current) {
        clearTimeout(ccSearchDebounceRef.current)
      }
      if (customerEmailDebounceRef.current) {
        clearTimeout(customerEmailDebounceRef.current)
      }
    }
  }, [])
  const [sqOwnerAttachments, setSqOwnerAttachments] = useState([])
  const [pendingAttachmentFiles, setPendingAttachmentFiles] = useState([])
  const [isUploadingAttachments, setIsUploadingAttachments] = useState(false)
  const attachmentInputRef = useRef(null)
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState('')
  const [isLoadingPdfPreview, setIsLoadingPdfPreview] = useState(false)
  const [materialCatalog, setMaterialCatalog] = useState([])
  const [isMaterialCatalogLoading, setIsMaterialCatalogLoading] = useState(false)
  const [materialCodeSearchTerms, setMaterialCodeSearchTerms] = useState({})
  const [showMaterialCodeDropdown, setShowMaterialCodeDropdown] = useState({})
  const [selectedMaterialCodeIndex, setSelectedMaterialCodeIndex] = useState({})
  const [materialCodeDropdownPos, setMaterialCodeDropdownPos] = useState({})
  // const [duplicateMaterialCodeIndexes, setDuplicateMaterialCodeIndexes] = useState(new Set())
  const materialCodeInputRefs = useRef({})

  const isQuoteActive =
    String(quoteRecordStatus || '').toLowerCase() === FINAL_STATUS.toLowerCase()

  const canAccessPreview = isQuoteActive || showPreviewStep

  const fetchNextQuoteNumber = async () => {
    setIsFetchingQuoteNumber(true)
    try {
      const response = await axios.post(API_URL, {
        action: 'get-next-quote-number'
      })
      const responseData = parseApiResponse(response.data)

      if (isApiSuccess(responseData) && responseData.data?.nextSequenceNumber) {
        setQuoteDetails((prev) => ({
          ...prev,
          quoteNumber: responseData.data.nextSequenceNumber
        }))
      } else {
        toast.error(responseData?.message || 'Failed to fetch quote number', {
          autoClose: 2000
        })
      }
    } catch (error) {
      console.error('Fetch next quote number error:', error)
      const errData = error.response?.data ? parseApiResponse(error.response.data) : null
      toast.error(
        errData?.message || 'Failed to fetch next quote number',
        { autoClose: 2000 }
      )
    } finally {
      setIsFetchingQuoteNumber(false)
    }
  }

  const loadQuoteForEdit = useCallback(async () => {
    if (!editId) return

    setIsLoadingQuote(true)
    try {
      const response = await axios.post(API_URL, {
        action: 'get-sales-quote-by-id',
        id: editId
      })
      const responseData = parseApiResponse(response.data)
      const quote = pickQuoteFromResponse(responseData, editId)

      if (!isApiSuccess(responseData) || !quote || typeof quote !== 'object') {
        toast.error(responseData?.message || 'Failed to load sales quote', { autoClose: 2000 })
        navigate('/salesQuotes')
        return
      }

      setQuoteId(quote._id || quote.id || editId)
      setQuoteRecordStatus(quote.status || '')
      setIsMailSent(Boolean(quote.isMailSent))
      setQuoteDetails({
        quoteNumber: quote.quoteNumber || '',
        quoteDate: parseDateFromApi(quote.quoteDate),
        quoteDeliveryDate: parseDateFromApi(quote.quoteDeliveryTime),
        quoteSummary: quote.quoteSummary || '',
        quoteCurrency: quote.quoteCurrency || 'INR',
        pdfLink: quote.pdfLink || '',
        pdfDownloadUrl: quote.pdfDownloadUrl || '',
        quoteDeliveryTimeDisplay: quote.quoteDeliveryTime || ''
      })
      setIsFetchingQuoteNumber(false)

      if (Array.isArray(quote.terms) && quote.terms.length > 0) {
        setTerms(
          quote.terms.map((t) => ({
            quoteTerm: t.quoteTerm || t.rfqTerm || '',
            quoteResponse: t.quoteResponse || t.termsResponse || ''
          }))
        )
      } else {
        setTerms(getDefaultTerms())
      }

      if (Array.isArray(quote.materials) && quote.materials.length > 0) {
        setMaterials(quote.materials.map(mapMaterialFromApi))
      } else {
        setMaterials([emptyMaterial()])
      }

      const customerList = Array.isArray(quote.customer)
        ? quote.customer
        : Array.isArray(quote.customers)
          ? quote.customers
          : []

      if (customerList.length > 0) {
        setCustomers(
          customerList.map((c) => ({
            customerId: c.customerId || c._id || '',
            customerName: c.customerName || '',
            customerMailId: c.customerMailId || '',
            phoneNumber: c.phoneNumber || c.phonenumber || '',
            emailLink: c.emailLink || ''
          }))
        )
      } else {
        setCustomers([emptyCustomer()])
      }

      const ownerAttachments = getSqOwnerListFromQuote(quote)
      if (ownerAttachments.length > 0) {
        setSqOwnerAttachments(ownerAttachments.map(mapSqOwnerFromApi))
      } else {
        setSqOwnerAttachments([])
      }
      setPendingAttachmentFiles([])

      if (String(quote.status || '').toLowerCase() === FINAL_STATUS.toLowerCase()) {
        setShowPreviewStep(true)
      }
    } catch (error) {
      console.error('Load sales quote error:', error)
      toast.error('Failed to load sales quote', { autoClose: 2000 })
      navigate('/salesQuotes')
    } finally {
      setIsLoadingQuote(false)
    }
  }, [editId, navigate])

  useEffect(() => {
    if (editId) {
      loadQuoteForEdit()
    } else {
      setQuoteDetails((prev) => ({ ...prev, quoteDate: getTodayIsoDate() }))
      fetchNextQuoteNumber()
    }
  }, [editId, loadQuoteForEdit])

  useEffect(() => {
    if (activeTab !== 'preview' || !quoteDetails.pdfLink?.trim()) {
      setPdfPreviewUrl('')
      return undefined
    }

    let cancelled = false
    setIsLoadingPdfPreview(true)
    resolveSqPdfDownloadUrl({
      pdfDownloadUrl: quoteDetails.pdfDownloadUrl,
      pdfLink: quoteDetails.pdfLink,
      quoteId
    })
      .then((url) => {
        if (!cancelled) {
          setPdfPreviewUrl(url)
          setQuoteDetails((prev) =>
            prev.pdfDownloadUrl === url ? prev : { ...prev, pdfDownloadUrl: url }
          )
        }
      })
      .catch((error) => {
        console.error('Load sales quote PDF preview error:', error)
        if (!cancelled) {
          setPdfPreviewUrl('')
          toast.error(error.message || 'Failed to load PDF preview', { autoClose: 2000 })
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoadingPdfPreview(false)
      })

    return () => {
      cancelled = true
    }
  }, [activeTab, quoteDetails.pdfLink, quoteDetails.pdfDownloadUrl, quoteId])

  useEffect(() => {
    const fetchUserEmail = async () => {
      const userId = localStorage.getItem('id')
      if (!userId) return
      try {
        const response = await axios.post(API_URL, {
          action: 'get-user',
          id: userId
        })
        const responseData = parseApiResponse(response.data)
        if (isApiSuccess(responseData) && responseData.user?.email) {
          setUserEmail(String(responseData.user.email).trim())
        }
      } catch (error) {
        console.error('Fetch user email error:', error)
      }
    }
    fetchUserEmail()
  }, [])

  const handleDetailsChange = (e) => {
    const { name, value } = e.target
    if (name === 'quoteNumber') return
    setQuoteDetails((prev) => ({ ...prev, [name]: value }))
    if (fieldErrors[name]) {
      setFieldErrors((prev) => {
        const next = { ...prev }
        delete next[name]
        return next
      })
    }
  }

  const clearFieldError = (fieldKey) => {
    setFieldErrors((prev) => {
      if (!prev[fieldKey]) return prev
      const next = { ...prev }
      delete next[fieldKey]
      return next
    })
  }

  const fieldControlClass = (fieldKey, extra = '') => {
    const invalid = fieldErrors[fieldKey] ? ' border-danger' : ''
    return `form-control${invalid}${extra ? ` ${extra}` : ''}`
  }

  const updateTerm = (index, field, value) => {
    if (field === 'quoteTerm') {
      const oldName = terms[index]?.quoteTerm
      if (oldName) clearFieldError(getTermFieldErrorKey(oldName))
    }
    setTerms((prev) => prev.map((row, i) => (i === index ? { ...row, [field]: value } : row)))
    if (field === 'quoteResponse') {
      const termName = terms[index]?.quoteTerm
      if (isMandatoryQuoteTerm(termName) && String(value || '').trim()) {
        clearFieldError(getTermFieldErrorKey(termName))
      }
    }
    if (field === 'quoteTerm' && isMandatoryQuoteTerm(value) && String(terms[index]?.quoteResponse || '').trim()) {
      clearFieldError(getTermFieldErrorKey(value))
    }
  }

  const addTermRowAfter = (index) => {
    setTerms((prev) => {
      if (prev.length === 0) return [emptyTerm()]
      const next = [...prev]
      next.splice(index + 1, 0, emptyTerm())
      return next
    })
  }

  const removeTermRow = (index) => {
    const termName = String(terms[index]?.quoteTerm || '').trim()
    if (termName) {
      clearFieldError(getTermFieldErrorKey(termName))
    }
    setTerms((prev) => prev.filter((_, i) => i !== index))
  }

  const updateCustomer = (index, field, value) => {
    setCustomers((prev) =>
      prev.map((row, i) => {
        if (i !== index) return row
        const next = { ...row, [field]: value }
        if (field === 'customerMailId') {
          next.emailLink = ''
        }
        return next
      })
    )
  }

  // --- Customer Mail ID autocomplete (lookup API; no customer_master screen access required) ---
  const searchCustomerContactEmails = async (term, requestId) => {
    try {
      const response = await axios.post(API_URL, {
        action: 'get-all-customers',
        page: 1,
        limit: 25,
        search: term,
        sortBy: 'createdAt',
        sortOrder: 'desc'
      })
      if (requestId !== customerEmailRequestIdRef.current) return

      const responseData = parseApiResponse(response.data)
      const list =
        responseData?.data?.customers ||
        responseData?.customers ||
        (Array.isArray(responseData?.data) ? responseData.data : [])
      if (!isApiSuccess(responseData) || !Array.isArray(list)) {
        setCustomerEmailSuggestions([])
        return
      }

      const contactRows = flattenCustomersToContactRows(list)
      const lower = term.toLowerCase()
      const matching = contactRows.filter(
        (row) =>
          String(row.email || '').toLowerCase().includes(lower) ||
          String(row.contactPerson || '').toLowerCase().includes(lower)
      )
      setCustomerEmailSuggestions(matching.length > 0 ? matching : contactRows)
    } catch (error) {
      console.error('Search customer contact emails error:', error)
      if (requestId === customerEmailRequestIdRef.current) {
        setCustomerEmailSuggestions([])
      }
    } finally {
      if (requestId === customerEmailRequestIdRef.current) {
        setIsSearchingCustomerEmails(false)
      }
    }
  }

  const handleCustomerMailIdChange = (index, value) => {
    updateCustomer(index, 'customerMailId', value)

    customerEmailRequestIdRef.current += 1
    const requestId = customerEmailRequestIdRef.current
    if (customerEmailDebounceRef.current) {
      clearTimeout(customerEmailDebounceRef.current)
    }

    const term = String(value || '').trim()
    if (term.length < 3) {
      setCustomerEmailSuggestIndex(null)
      setCustomerEmailSuggestions([])
      setIsSearchingCustomerEmails(false)
      return
    }

    setCustomerEmailSuggestIndex(index)
    setCustomerEmailSuggestions([])
    setIsSearchingCustomerEmails(true)
    customerEmailDebounceRef.current = setTimeout(() => {
      searchCustomerContactEmails(term, requestId)
    }, 400)
  }

  const handleSelectCustomerEmail = (index, contactRow) => {
    const email = String(contactRow?.email || '').trim()
    if (!email) return

    setCustomers((prev) =>
      prev.map((row, i) => {
        if (i !== index) return row
        const next = { ...row, customerMailId: email, emailLink: '' }
         if (contactRow.customerId) {
        next.customerId = contactRow.customerId
      }
        if (!String(row.customerName || '').trim() && contactRow.customerName) {
          next.customerName = contactRow.customerName
        }
        if (!String(row.phoneNumber || '').trim() && contactRow.contactNumber) {
          next.phoneNumber = normalizePhoneNumber(contactRow.contactNumber)
        }
        return next
      })
    )
    customerEmailRequestIdRef.current += 1
    setCustomerEmailSuggestIndex(null)
    setCustomerEmailSuggestions([])
    setIsSearchingCustomerEmails(false)
  }

  const handleCustomerMailIdBlur = () => {
    // Delay so suggestion clicks (onMouseDown) land first
    setTimeout(() => {
      setCustomerEmailSuggestIndex(null)
      setCustomerEmailSuggestions([])
      setIsSearchingCustomerEmails(false)
    }, 180)
  }

  // --- Additional CC email autocomplete (lookups for sales_quotes; no user/customer module required) ---
  const getCcSearchTerm = (value) => {
    const segments = String(value || '').split(/[,;]/)
    return segments[segments.length - 1].trim()
  }

  const searchCcUsers = async (term, requestId) => {
    try {
      // Search internal users and customer contacts in parallel so
      // customer emails (e.g. MK59@sanmargroup.com) also appear as CC suggestions
      const [usersResult, customersResult] = await Promise.allSettled([
        axios.post(API_URL, {
          action: 'get-users',
          page: 1,
          limit: 25,
          sortBy: 'createdAt',
          sortOrder: 'desc',
          search: term
        }),
        axios.post(API_URL, {
          action: 'get-all-customers',
          page: 1,
          limit: 25,
          search: term,
          sortBy: 'createdAt',
          sortOrder: 'desc'
        })
      ])
      if (requestId !== ccSearchRequestIdRef.current) return

      const suggestions = []
      const seen = new Set()
      const addSuggestion = (name, email, sourceLabel) => {
        const trimmed = String(email || '').trim()
        if (!isValidEmailAddress(trimmed)) return
        const key = trimmed.toLowerCase()
        if (seen.has(key)) return
        seen.add(key)
        suggestions.push({
          name: String(name || '').trim() || trimmed,
          email: trimmed,
          sourceLabel
        })
      }

      if (usersResult.status === 'fulfilled') {
        const responseData = parseApiResponse(usersResult.value.data)
        const users = Array.isArray(responseData?.users)
          ? responseData.users
          : Array.isArray(responseData?.data?.users)
            ? responseData.data.users
            : []
        users.forEach((user) => addSuggestion(user?.name, user?.email, 'User'))
      }

      if (customersResult.status === 'fulfilled') {
        const responseData = parseApiResponse(customersResult.value.data)
        const list =
          responseData?.data?.customers ||
          responseData?.customers ||
          (Array.isArray(responseData?.data) ? responseData.data : [])
        if (Array.isArray(list)) {
          const lower = term.toLowerCase()
          const contactRows = flattenCustomersToContactRows(list)
          const matching = contactRows.filter(
            (row) =>
              String(row.email || '').toLowerCase().includes(lower) ||
              String(row.contactPerson || '').toLowerCase().includes(lower) ||
              String(row.customerName || '').toLowerCase().includes(lower)
          )
          ;(matching.length > 0 ? matching : contactRows).forEach((row) =>
            addSuggestion(
              row.contactPerson || row.customerName,
              row.email,
              row.customerName || 'Customer'
            )
          )
        }
      }

      setCcUserSuggestions(suggestions)
    } catch (error) {
      console.error('Search CC users error:', error)
      if (requestId === ccSearchRequestIdRef.current) {
        setCcUserSuggestions([])
      }
    } finally {
      if (requestId === ccSearchRequestIdRef.current) {
        setIsSearchingCcUsers(false)
      }
    }
  }

  const handleAdditionalCcChange = (e) => {
    const value = e.target.value
    setAdditionalCcEmails(value)

    ccSearchRequestIdRef.current += 1
    const requestId = ccSearchRequestIdRef.current
    if (ccSearchDebounceRef.current) {
      clearTimeout(ccSearchDebounceRef.current)
    }

    const term = getCcSearchTerm(value)
    if (term.length < 3) {
      setCcUserSuggestions([])
      setIsSearchingCcUsers(false)
      return
    }

    setIsSearchingCcUsers(true)
    ccSearchDebounceRef.current = setTimeout(() => {
      searchCcUsers(term, requestId)
    }, 400)
  }

  const handleSelectCcUser = (email) => {
    const selected = String(email || '').trim()
    if (!selected) return

    setAdditionalCcEmails((prev) => {
      const value = String(prev || '')
      const lastComma = Math.max(value.lastIndexOf(','), value.lastIndexOf(';'))
      const base = lastComma >= 0 ? `${value.slice(0, lastComma + 1)} ` : ''
      const existing = parseAdditionalCcEmails(base)
      if (
        existing.some((item) => item.toLowerCase() === selected.toLowerCase())
      ) {
        return `${base.trim()} `.trimStart()
      }
      return `${base}${selected}, `
    })
    ccSearchRequestIdRef.current += 1
    setCcUserSuggestions([])
    setIsSearchingCcUsers(false)
  }

  const handleAdditionalCcBlur = () => {
    // Delay so suggestion clicks (onMouseDown) land first
    setTimeout(() => {
      setCcUserSuggestions([])
      setIsSearchingCcUsers(false)
    }, 180)
  }

  const addCustomerRowAfter = (index) => {
    setCustomers((prev) => {
      const next = [...prev]
      next.splice(index + 1, 0, emptyCustomer())
      return next
    })
  }

  const handleGstChange = (index, field, value) => {
  const updated = [...materials];

  updated[index][field] = value;

  const { gstValue, totalValue } = calcMaterialPricing(
    updated[index].basicPrice,
    updated[index].quantity,
    updated[index].gst
  );

  updated[index].gstValue = gstValue.toFixed(2);
  updated[index].totalValue = totalValue.toFixed(2);

  setMaterials(updated);
};

  const removeCustomerRow = (index) => {
    if (customers.length <= 1) {
      setCustomers([emptyCustomer()])
      return
    }
    setCustomers((prev) => prev.filter((_, i) => i !== index))
  }

  const updateMaterial = (index, field, value) => {
    setMaterials((prev) =>
      prev.map((row, i) => {
        if (i !== index) return row
        const next = { ...row, [field]: value }
        if (field === 'basicPrice' || field === 'quantity' || field === 'gst') {
          next.totalValue = calcMaterialLineTotal(
            next.basicPrice,
            next.quantity,
            next.gst
          )
        }
        return next
      })
    )
  }

  const handleMaterialDecimalChange = (index, field, value) => {
    if (!isDecimalInput(value)) return
    updateMaterial(index, field, value)
  }

  const handleQuantityChange = (index, value) => {
    if (value === '' || isDecimalInput(value)) {
      updateMaterial(index, 'quantity', value)
    }
  }

  const addMaterialRowAfter = (index) => {
    setMaterials((prev) => {
      const next = [...prev]
      next.splice(index + 1, 0, emptyMaterial())
      return next
    })
    setActiveMaterialRowIndex(index + 1)
  }

  const removeMaterialRow = (index) => {
    if (materials.length <= 1) {
      toast.error('At least one material row is required', { autoClose: 2000 })
      return
    }
    setMaterials((prev) => prev.filter((_, i) => i !== index))
    setActiveMaterialRowIndex((prev) => {
      if (prev === index) return Math.max(0, index - 1)
      if (prev > index) return prev - 1
      return prev
    })
  }

  const procurementToMaterialRow = (procurement) => {
   const basicPrice =
    procurement.basicPrice != null && procurement.basicPrice !== '' && Number(procurement.basicPrice) !== 0
      ? String(procurement.basicPrice)
      : ''
  const gst = '18'
    const quantity =
      procurement.quantity != null && procurement.quantity !== ''
        ? String(procurement.quantity)
        : ''
    const totalFromApi =
      procurement.totalValue != null && procurement.totalValue !== ''
        ? String(procurement.totalValue)
        : ''

    return {
      materialCode: procurement.materialCode || '-',
      materialDescription: procurement.materialDescription || '',
      hsn: procurement.hsnCode || procurement.hsn || '-',
      uom: procurement.uom || '',
      quantity,
      prNumber: String(procurement.prNo || procurement.prNumber || '').trim(),
      pr_id: String(procurement._id || procurement.pr_id || procurement.id || '').trim(),
      clientReference: String(procurement.clientReference || '').trim(),
      technicalRemarks: formatTechnicalRemarksFromApi(procurement),
      basicPrice,
      gst,
      totalValue:
        calcMaterialLineTotal(basicPrice, quantity, gst) || totalFromApi,
      quotePrice:
        procurement.quotePrice != null && procurement.quotePrice !== ''
          ? String(procurement.quotePrice)
          : ''
    }
  }

  const getSelectablePrKeys = () =>
    prSearchResults
      .map((item, index) => ({ item, key: getProcurementKey(item, index) }))
      .filter(({ item }) => !isProcurementInMaterials(materials, item))
      .map(({ key }) => key)

  const countPendingPrSelection = () =>
    prSearchResults.filter(
      (item, index) =>
        selectedPrKeys.has(getProcurementKey(item, index)) &&
        !isProcurementInMaterials(materials, item)
    ).length

  const countAddedInCurrentResults = () =>
    prSearchResults.filter((item) => isProcurementInMaterials(materials, item)).length

  const togglePrSelection = (key) => {
    const procurement = prSearchResults.find(
      (item, index) => getProcurementKey(item, index) === key
    )
    if (procurement && isProcurementInMaterials(materials, procurement)) return
    setSelectedPrKeys((prev) => {
      const next = new Set(prev)
      if (next.has(key)) {
        next.delete(key)
      } else {
        next.add(key)
      }
      return next
    })
  }

  const toggleSelectAllPrResults = () => {
    const selectable = getSelectablePrKeys()
    if (selectable.length === 0) return

    const allSelectableSelected = selectable.every((key) => selectedPrKeys.has(key))
    setSelectedPrKeys((prev) => {
      const next = new Set(prev)
      if (allSelectableSelected) {
        selectable.forEach((key) => next.delete(key))
      } else {
        selectable.forEach((key) => next.add(key))
      }
      return next
    })
  }

  const fetchMaterialCatalog = useCallback(async () => {
  setIsMaterialCatalogLoading(true)
  try {
    const response = await axios.post(API_URL, {
      action: 'get-all-material',
      page: 1,
      limit: 5000
    })
    const responseData = parseApiResponse(response.data)
    if (isApiSuccess(responseData)) {
      setMaterialCatalog(responseData.data?.materials || [])
    } else {
      setMaterialCatalog([])
    }
  } catch (error) {
    console.error('Fetch material catalog error:', error)
    setMaterialCatalog([])
  } finally {
    setIsMaterialCatalogLoading(false)
  }
}, [])

useEffect(() => {
  fetchMaterialCatalog()
}, [fetchMaterialCatalog])

useEffect(() => {
  setMaterialCodeSearchTerms((prev) => {
    const next = { ...prev }
    materials.forEach((row, index) => {
      next[index] = row.materialCode || ''
    })
    return next
  })
}, [materials])

const getFilteredMaterialCatalog = (rowIndex) => {
  const search = (materialCodeSearchTerms[rowIndex] || '').toLowerCase()
  return materialCatalog.filter(
    (m) =>
      m.materialCode?.toLowerCase().includes(search) ||
      m.productName?.toLowerCase().includes(search) ||
      m.materialDescription?.toLowerCase().includes(search)
  )
}


const openMaterialCodeDropdown = useCallback((rowIndex) => {
  if (isMaterialCatalogLoading) return
  const el = materialCodeInputRefs.current[rowIndex]
  if (el) {
    const rect = el.getBoundingClientRect()
    setMaterialCodeDropdownPos((prev) => ({
      ...prev,
      [rowIndex]: { top: rect.bottom, left: rect.left, width: rect.width }
    }))
  }
  setShowMaterialCodeDropdown((prev) => ({ ...prev, [rowIndex]: true }))
}, [isMaterialCatalogLoading])

const closeMaterialCodeDropdown = (rowIndex) => {
  setShowMaterialCodeDropdown((prev) => ({ ...prev, [rowIndex]: false }))
  setSelectedMaterialCodeIndex((prev) => ({ ...prev, [rowIndex]: -1 }))
}

const handleMaterialCodeSearchChange = (rowIndex, value) => {
  setMaterialCodeSearchTerms((prev) => ({ ...prev, [rowIndex]: value }))
  setSelectedMaterialCodeIndex((prev) => ({ ...prev, [rowIndex]: -1 }))
  openMaterialCodeDropdown(rowIndex)
  updateMaterial(rowIndex, 'materialCode', value)

}

const handleMaterialCodeSelect = (rowIndex, material) => {

  setMaterials((prev) =>
    prev.map((row, i) => {
      if (i !== rowIndex) return row
      const next = {
        ...row,
        materialCode: material.materialCode || '',
        materialDescription:
          material.productName || material.materialDescription || row.materialDescription,
        hsn: material.hsnCode || material.hsn || row.hsn,
        uom: material.uom || row.uom,
        gst: String(row.gst || '').trim() ? row.gst : '18'
      }
      next.totalValue = calcMaterialLineTotal(next.basicPrice, next.quantity, next.gst)
      return next
    })
  )

  setMaterialCodeSearchTerms((prev) => ({ ...prev, [rowIndex]: material.materialCode }))
  closeMaterialCodeDropdown(rowIndex)
}

const handleMaterialCodeKeyDown = (rowIndex, e) => {
  if (!showMaterialCodeDropdown[rowIndex]) return
  const list = getFilteredMaterialCatalog(rowIndex)
  const currentIndex = selectedMaterialCodeIndex[rowIndex] ?? -1

  if (e.key === 'ArrowDown') {
    e.preventDefault()
    setSelectedMaterialCodeIndex((prev) => ({
      ...prev,
      [rowIndex]: currentIndex < list.length - 1 ? currentIndex + 1 : currentIndex
    }))
  } else if (e.key === 'ArrowUp') {
    e.preventDefault()
    setSelectedMaterialCodeIndex((prev) => ({
      ...prev,
      [rowIndex]: currentIndex > 0 ? currentIndex - 1 : 0
    }))
  } else if (e.key === 'Enter') {
    if (currentIndex >= 0 && list[currentIndex]) {
      e.preventDefault()
      handleMaterialCodeSelect(rowIndex, list[currentIndex])
    }
  }
}

useEffect(() => {
  const activeRowIndex = Object.keys(showMaterialCodeDropdown).find(
    (idx) => showMaterialCodeDropdown[idx]
  )
  if (activeRowIndex === undefined) return

  const reposition = () => openMaterialCodeDropdown(activeRowIndex)
  window.addEventListener('scroll', reposition, true)
  window.addEventListener('resize', reposition)
  return () => {
    window.removeEventListener('scroll', reposition, true)
    window.removeEventListener('resize', reposition)
  }
}, [showMaterialCodeDropdown, openMaterialCodeDropdown])

const getFilteredMaterialDescCatalog = (rowIndex) => {
  const search = (materials[rowIndex]?.materialDescription || '').toLowerCase()
  return materialCatalog.filter((m) => {
    return (
      (m.productName || '').toLowerCase().includes(search) ||
      (m.categoryName || '').toLowerCase().includes(search) ||
      (m.subcategoryName || '').toLowerCase().includes(search) ||
      (m.partNumber || '').toLowerCase().includes(search) ||
      (m.makeBrandOem || '').toLowerCase().includes(search)
    )
  })
}

const openMaterialDescDropdown = useCallback((rowIndex) => {
  if (isMaterialCatalogLoading) return
  const el = materialDescInputRefs.current[rowIndex]
  if (el) {
    const rect = el.getBoundingClientRect()
    setMaterialDescDropdownPos((prev) => ({
      ...prev,
      [rowIndex]: { top: rect.bottom, left: rect.left, width: rect.width }
    }))
  }
  setShowMaterialDescDropdown((prev) => ({ ...prev, [rowIndex]: true }))
}, [isMaterialCatalogLoading])

const closeMaterialDescDropdown = (rowIndex) => {
  setShowMaterialDescDropdown((prev) => ({ ...prev, [rowIndex]: false }))
  setSelectedMaterialDescIndex((prev) => ({ ...prev, [rowIndex]: -1 }))
}

const handleMaterialDescSearchChange = (rowIndex, value) => {
  updateMaterial(rowIndex, 'materialDescription', value)
  setSelectedMaterialDescIndex((prev) => ({ ...prev, [rowIndex]: -1 }))
  openMaterialDescDropdown(rowIndex)
}

const handleMaterialDescSelect = (rowIndex, material) => {
  setMaterials((prev) =>
    prev.map((row, i) => {
      if (i !== rowIndex) return row
      const next = {
        ...row,
        materialCode: material.materialCode || row.materialCode,
        materialDescription:
          material.productName || material.materialDescription || row.materialDescription,
        hsn: material.hsnCode || material.hsn || row.hsn,
        uom: material.uom || row.uom,
        gst: String(row.gst || '').trim() ? row.gst : '18'
      }
      next.totalValue = calcMaterialLineTotal(next.basicPrice, next.quantity, next.gst)
      return next
    })
  )

  setMaterialCodeSearchTerms((prev) => ({ ...prev, [rowIndex]: material.materialCode || '' }))
  closeMaterialDescDropdown(rowIndex)
}

const handleMaterialDescKeyDown = (rowIndex, e) => {
  if (!showMaterialDescDropdown[rowIndex]) return
  const list = getFilteredMaterialDescCatalog(rowIndex)
  const currentIndex = selectedMaterialDescIndex[rowIndex] ?? -1

  if (e.key === 'ArrowDown') {
    e.preventDefault()
    setSelectedMaterialDescIndex((prev) => ({
      ...prev,
      [rowIndex]: currentIndex < list.length - 1 ? currentIndex + 1 : currentIndex
    }))
  } else if (e.key === 'ArrowUp') {
    e.preventDefault()
    setSelectedMaterialDescIndex((prev) => ({
      ...prev,
      [rowIndex]: currentIndex > 0 ? currentIndex - 1 : 0
    }))
  } else if (e.key === 'Enter') {
    if (currentIndex >= 0 && list[currentIndex]) {
      e.preventDefault()
      handleMaterialDescSelect(rowIndex, list[currentIndex])
    }
  } else if (e.key === 'Escape') {
    closeMaterialDescDropdown(rowIndex)
  }
}

useEffect(() => {
  const activeRowIndex = Object.keys(showMaterialDescDropdown).find(
    (idx) => showMaterialDescDropdown[idx]
  )
  if (activeRowIndex === undefined) return

  const reposition = () => openMaterialDescDropdown(activeRowIndex)
  window.addEventListener('scroll', reposition, true)
  window.addEventListener('resize', reposition)
  return () => {
    window.removeEventListener('scroll', reposition, true)
    window.removeEventListener('resize', reposition)
  }
}, [showMaterialDescDropdown, openMaterialDescDropdown])

  const applySelectedProcurements = () => {
    const itemsToAdd = prSearchResults
      .map((item, index) => ({
        item,
        key: getProcurementKey(item, index)
      }))
      .filter(
        ({ key, item }) =>
          selectedPrKeys.has(key) && !isProcurementInMaterials(materials, item)
      )

    if (itemsToAdd.length === 0) {
      toast.error('Select new items that are not already added', { autoClose: 2000 })
      return
    }

    let nextActiveRow = 0
    setMaterials((prev) => {
      const next = [...prev]
      let writeIndex = findNextMaterialWriteIndex(next)

      itemsToAdd.forEach(({ item }) => {
        const rowData = procurementToMaterialRow(item)
        if (writeIndex < next.length) {
          next[writeIndex] = rowData
        } else {
          next.push(rowData)
        }
        writeIndex += 1
      })

      nextActiveRow = writeIndex
      return next
    })
    setActiveMaterialRowIndex(nextActiveRow)

    setSelectedPrKeys((prev) => {
      const next = new Set(prev)
      itemsToAdd.forEach(({ key }) => next.delete(key))
      return next
    })

    setPrSearchResultsOpen(false)
    toast.success(`${itemsToAdd.length} material row(s) added`, { autoClose: 2000 })
  }

  const PR_SEARCH_MIN_LENGTH = 3
  const PR_SEARCH_DEBOUNCE_MS = 400

  const searchProcurementsByPrNo = useCallback(
    async (searchTerm, options = {}) => {
      const { showToast = true } = options
      const prNo = String(searchTerm ?? '').trim()

      if (!prNo) {
        setPrSearchResults([])
        setPrSearchResultsOpen(false)
        if (showToast) {
          toast.error('Enter PR number to search', { autoClose: 2000 })
        }
        return
      }

      if (prNo.length < PR_SEARCH_MIN_LENGTH) {
        setPrSearchResults([])
        setPrSearchResultsOpen(false)
        return
      }

      const requestId = ++prSearchRequestIdRef.current
      setPrSearchLoading(true)

      try {
        // LOOKUP_API_ACTIONS: PR picker — needs sales_quotes access, not procurement_tracker
        const response = await axios.post(API_URL, {
          action: 'get-all-procurements',
          page: 1,
          limit: 5000,
          prNo
        })

        if (requestId !== prSearchRequestIdRef.current) {
          return
        }

        const responseData = parseApiResponse(response.data)
        const list = responseData?.data?.procurements || []

        if (isApiSuccess(responseData) && Array.isArray(list) && list.length > 0) {
          setPrSearchResults(list)
          setPrSearchResultsOpen(true)
          setSelectedPrKeys((prev) => {
            const next = new Set(prev)
            list.forEach((item, index) => {
              const key = getProcurementKey(item, index)
              if (isProcurementInMaterials(materials, item)) {
                next.delete(key)
              }
            })
            return next
          })
          if (showToast) {
            toast.info(`${list.length} item(s) found.`, { autoClose: 2500 })
          }
        } else {
          setPrSearchResults([])
          setPrSearchResultsOpen(false)
          if (showToast) {
            toast.info(
              responseData?.message || 'No procurement items found for this PR number',
              { autoClose: 2000 }
            )
          }
        }
      } catch (error) {
        if (requestId !== prSearchRequestIdRef.current) {
          return
        }
        console.error('PR search error:', error)
        setPrSearchResults([])
        setPrSearchResultsOpen(false)
        const errData = error.response?.data ? parseApiResponse(error.response.data) : null
        toast.error(
          errData?.message || 'Failed to search procurement by PR number',
          { autoClose: 2000 }
        )
      } finally {
        if (requestId === prSearchRequestIdRef.current) {
          setPrSearchLoading(false)
        }
      }
    },
    [materials]
  )

  const handlePrSearchChange = (e) => {
    const value = e.target.value
    setPrNoSearch(value)
    prSearchRequestIdRef.current += 1

    if (prSearchDebounceRef.current) {
      clearTimeout(prSearchDebounceRef.current)
    }

    const trimmed = value.trim()
    if (trimmed.length < PR_SEARCH_MIN_LENGTH) {
      setPrSearchLoading(false)
      setPrSearchResults([])
      setPrSearchResultsOpen(false)
      return
    }

    prSearchDebounceRef.current = setTimeout(() => {
      searchProcurementsByPrNo(trimmed, { showToast: false })
    }, PR_SEARCH_DEBOUNCE_MS)
  }

  const handlePrNoSearch = () => {
    if (prSearchDebounceRef.current) {
      clearTimeout(prSearchDebounceRef.current)
    }
    searchProcurementsByPrNo(prNoSearch, { showToast: true })
  }

  const handlePrSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handlePrNoSearch()
    }
  }

  const CUSTOMER_SEARCH_MIN_LENGTH = 3
  const CUSTOMER_SEARCH_DEBOUNCE_MS = 400

  const getAddedCustomerEmails = () =>
    new Set(
      customers
        .map((c) => c.customerMailId.trim().toLowerCase())
        .filter(Boolean)
    )

  const isCustomerAlreadyAdded = (email) =>
    getAddedCustomerEmails().has(String(email || '').trim().toLowerCase())

  const searchCustomers = useCallback(async (searchTerm, options = {}) => {
    const { showToast = true } = options
    const search = String(searchTerm ?? '').trim()

    if (!search) {
      setCustomerSearchResults([])
      if (showToast) {
        toast.error('Enter customer name or email to search', { autoClose: 2000 })
      }
      return
    }

    if (search.length < CUSTOMER_SEARCH_MIN_LENGTH) {
      setCustomerSearchResults([])
      return
    }

    const requestId = ++customerSearchRequestIdRef.current
    setCustomerSearchLoading(true)

    try {
      const response = await axios.post(API_URL, {
        action: 'get-all-customers',
        search
      })

      if (requestId !== customerSearchRequestIdRef.current) {
        return
      }

      const responseData = parseApiResponse(response.data)
      const list =
        responseData?.data?.customers ||
        responseData?.customers ||
        (Array.isArray(responseData?.data) ? responseData.data : [])

      if (isApiSuccess(responseData) && Array.isArray(list) && list.length > 0) {
        const contactRows = flattenCustomersToContactRows(list)
        setCustomerSearchResults(contactRows)
        if (showToast) {
          toast.info(
            contactRows.length > 0
              ? `${contactRows.length} contact(s) found.`
              : 'No customer contacts with email found',
            { autoClose: 2500 }
          )
        }
      } else {
        setCustomerSearchResults([])
        if (showToast) {
          toast.info(responseData?.message || 'No customers found', { autoClose: 2000 })
        }
      }
    } catch (error) {
      if (requestId !== customerSearchRequestIdRef.current) {
        return
      }
      console.error('Customer search error:', error)
      const errData = error.response?.data ? parseApiResponse(error.response.data) : null
      toast.error(errData?.message || 'Failed to search customers', { autoClose: 2000 })
      setCustomerSearchResults([])
    } finally {
      if (requestId === customerSearchRequestIdRef.current) {
        setCustomerSearchLoading(false)
      }
    }
  }, [])

  const handleCustomerSearchChange = (e) => {
    const value = e.target.value
    setCustomerSearch(value)
    customerSearchRequestIdRef.current += 1

    if (customerSearchDebounceRef.current) {
      clearTimeout(customerSearchDebounceRef.current)
    }

    const trimmed = value.trim()
    if (trimmed.length < CUSTOMER_SEARCH_MIN_LENGTH) {
      setCustomerSearchLoading(false)
      setCustomerSearchResults([])
      return
    }

    customerSearchDebounceRef.current = setTimeout(() => {
      searchCustomers(trimmed, { showToast: false })
    }, CUSTOMER_SEARCH_DEBOUNCE_MS)
  }

  const handleCustomerSearch = () => {
    if (customerSearchDebounceRef.current) {
      clearTimeout(customerSearchDebounceRef.current)
    }
    searchCustomers(customerSearch, { showToast: true })
  }

  const handleCustomerSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleCustomerSearch()
    }
  }

  const selectCustomer = (contactRow) => {
    const email = String(contactRow?.email || '').trim()
    const name = String(contactRow?.customerName || '').trim()
    if (!email) {
      toast.error('This contact has no email on record', { autoClose: 2000 })
      return
    }
    if (isCustomerAlreadyAdded(email)) {
      toast.info('Customer already added', { autoClose: 2000 })
      return
    }
    setCustomers((prev) => {
      const emptyIdx = prev.findIndex(
        (c) => !c.customerName.trim() && !c.customerMailId.trim()
      )
      const phone = String(contactRow?.contactNumber || '').trim()
      const row = {   customerId: contactRow?.customerId || '', customerName: name, customerMailId: email, phoneNumber: phone, emailLink: '' }
      if (emptyIdx === -1) return [...prev, row]
      const next = prev.map((c, i) => (i === emptyIdx ? row : c))
      const hasEmptyRow = next.some(
        (c) => !c.customerName.trim() && !c.customerMailId.trim()
      )
      return hasEmptyRow ? next : [...next, emptyCustomer()]
    })
    toast.success('Customer added', { autoClose: 1500 })
  }

  const validateDetails = () => {
    const errors = {}

    if (isFetchingQuoteNumber) {
      errors.quoteNumber = 'Quote number is still loading. Please wait.'
    } else if (!quoteDetails.quoteNumber.trim()) {
      errors.quoteNumber = 'Quote number is required'
    }
    if (!quoteDetails.quoteDate) {
      errors.quoteDate = 'Quote date is required'
    }
    if (!quoteDetails.quoteDeliveryDate) {
      errors.quoteDeliveryDate = 'Delivery date is required'
    } else if (
      quoteDetails.quoteDate &&
      isDeliveryBeforeQuoteDate(quoteDetails.quoteDate, quoteDetails.quoteDeliveryDate)
    ) {
      errors.quoteDeliveryDate = 'Delivery date cannot be before quote date'
    }
    if (!quoteDetails.quoteSummary.trim()) {
      errors.quoteSummary = 'Quote summary is required'
    }
    if (!quoteDetails.quoteCurrency.trim()) {
      errors.quoteCurrency = 'Currency is required'
    }
    for (const termName of MANDATORY_QUOTE_TERM_NAMES) {
      const term = terms.find(
        (t) =>
          String(t.quoteTerm || '').trim().toLowerCase() === termName.toLowerCase()
      )
      if (term && !String(term.quoteResponse || '').trim()) {
        errors[getTermFieldErrorKey(termName)] = `${termName} is required`
      }
    }

    setFieldErrors(errors)
    return Object.keys(errors).length === 0
  }
  const validateMaterials = () => {
    const filled = materials.filter(
      (m) =>
        m.materialCode.trim() ||
        m.materialDescription.trim() ||
        m.quantity !== ''
    )
    if (filled.length === 0) {
      toast.error('Add at least one material line', { autoClose: 2000 })
      return false
    }
 for (let i = 0; i < filled.length; i++) {
    const row = filled[i]
    const idx = materials.indexOf(row) + 1
    if (!row.materialCode.trim()) {
      toast.error(`Row ${idx}: Material code is required`, { autoClose: 2000 })
      return false
    }
    if (!row.materialDescription.trim()) {
      toast.error(`Row ${idx}: Material description is required`, { autoClose: 2000 })
      return false
    }
    if (row.quantity === '' || isNaN(parseFloat(row.quantity))) {
      toast.error(`Row ${idx}: Valid quantity is required`, { autoClose: 2000 })
      return false
    }
    if (row.basicPrice === '' || isNaN(parseFloat(row.basicPrice))) {
      toast.error(`Row ${idx}: Basic price is required`, { autoClose: 2000 })
      return false
    }
    if (row.gst === '' || isNaN(parseFloat(row.gst))) {
      toast.error(`Row ${idx}: GST % is required`, { autoClose: 2000 })
      return false
    }
    if (!String(row.technicalRemarks || '').trim()) {
      toast.error(`Row ${idx}: Technical remarks is required`, { autoClose: 2000 })
      return false
    }
  }


  return true
}

  const validateCustomerTab = () => {
    const filled = customers.filter(
      (c) => c.customerName.trim() && c.customerMailId.trim()
    )
    if (filled.length === 0) {
      toast.error('Add at least one customer with name and email', { autoClose: 2000 })
      return false
    }
    for (let i = 0; i < filled.length; i++) {
      const row = filled[i]
      const idx = customers.indexOf(row) + 1
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(row.customerMailId.trim())) {
        toast.error(`Customer row ${idx}: Enter a valid email`, { autoClose: 2000 })
        return false
      }
    }
    return true
  }

  const buildTermsPayload = () =>
    terms
      .filter((t) => t.quoteTerm.trim())
      .map((t) => ({
        quoteTerm: t.quoteTerm.trim(),
        quoteResponse: (t.quoteResponse || '').trim()
      }))

  const buildMaterialsPayload = (rows = materials) =>
    rows
      .filter(
        (m) =>
          m.materialCode.trim() ||
          m.materialDescription.trim() ||
          m.quantity !== ''
      )
      .map((m) => {
        const row = {
          materialCode: m.materialCode.trim(),
          materialDescription: m.materialDescription.trim(),
          hsn: m.hsn.trim(),
          uom: m.uom.trim(),
          quantity: parseFloat(m.quantity) || 0
        }
        const materialId = String(m.materialId || '').trim()
        if (materialId) row.materialId = materialId
        const sno = Number(m.sno)
        if (Number.isFinite(sno) && sno > 0) row.sno = sno
        const prNumber = String(m.prNumber || '').trim()
        if (prNumber) row.prNumber = prNumber
        const prId = String(m.pr_id || '').trim()
        if (prId) row.pr_id = prId
        const clientReference = String(m.clientReference || '').trim()
        if (clientReference) row.clientReference = clientReference
        const technicalRemarks = String(m.technicalRemarks || '').trim()
        if (technicalRemarks) row.technicalRemarks = technicalRemarks

        if (m.basicPrice !== '' && Number.isFinite(parseFloat(m.basicPrice))) {
          row.basicPrice = parseFloat(m.basicPrice)
        }
        if (m.gst !== '' && Number.isFinite(parseFloat(m.gst))) {
          row.gstPercent = parseFloat(m.gst)
        }
        const pricing = calcMaterialPricing(m.basicPrice, m.quantity, m.gst)
        if (pricing.gstValue > 0 || (m.gst !== '' && Number.isFinite(parseFloat(m.gst)))) {
          row.gstValue = pricing.gstValue
        }
        if (m.totalValue !== '' && Number.isFinite(parseFloat(m.totalValue))) {
          row.totalValue = parseFloat(m.totalValue)
        } else if (pricing.totalValue > 0) {
          row.totalValue = pricing.totalValue
        }
        if (m.quotePrice !== '' && Number.isFinite(parseFloat(m.quotePrice))) {
          row.quotePrice = parseFloat(m.quotePrice)
        }

        return row
      })

  const buildMaterialsSaveFields = (rows = materials) => {
    const filledRows = rows.filter(
      (m) =>
        m.materialCode.trim() ||
        m.materialDescription.trim() ||
        m.quantity !== ''
    )
    const gross = calcGrossTotalsFromRows(filledRows)
    return {
      materials: buildMaterialsPayload(rows),
      grossTotalBasicPrice: gross.grossTotalBasicPrice,
      grossGstValue: gross.grossGstValue,
      grossTotalValue: gross.grossTotalValue
    }
  }

  const buildCustomersPayload = () =>
    customers
      .filter((c) => c.customerName.trim() || c.customerMailId.trim())
      .map((c) => {
        const row = {
          customerName: c.customerName.trim(),
          customerMailId: c.customerMailId.trim()
        }
        const phoneNumber = String(c.phoneNumber || '').trim()
        if (phoneNumber) row.phoneNumber = phoneNumber
        const customerId = String(c.customerId || '').trim()
        if (customerId) row.customerId = customerId
        return row
      })

  const getFilledMaterials = useCallback(
    () =>
      materials.filter(
        (m) =>
          m.materialCode.trim() ||
          m.materialDescription.trim() ||
          m.quantity !== ''
      ),
    [materials]
  )

  const materialGrossTotals = useMemo(() => {
    return calcGrossTotalsFromRows(getFilledMaterials())
  }, [getFilledMaterials])

  const getFilledCustomers = () => buildCustomersPayload()

  const hasCompleteDetails = () => {
    if (isFetchingQuoteNumber || !quoteDetails.quoteNumber.trim()) return false
    if (!quoteDetails.quoteDate || !quoteDetails.quoteDeliveryDate) return false
    if (isDeliveryBeforeQuoteDate(quoteDetails.quoteDate, quoteDetails.quoteDeliveryDate)) {
      return false
    }
    if (!quoteDetails.quoteSummary.trim() || !quoteDetails.quoteCurrency.trim()) return false
    return true
  }

  const hasCompleteMaterials = () => {
    const filled = getFilledMaterials()
    if (filled.length === 0) return false
    return filled.every(
      (row) =>
        row.materialCode.trim() &&
        row.materialDescription.trim() &&
        row.quantity !== '' &&
        !isNaN(parseFloat(row.quantity))
    )
  }

  const hasCompleteCustomers = () => {
    const filled = customers.filter(
      (c) => c.customerName.trim() && c.customerMailId.trim()
    )
    if (filled.length === 0) return false
    return filled.every((row) =>
      /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(row.customerMailId.trim())
    )
  }

  const isReadyToSendEmail =
    canAccessPreview &&
    hasSalesQuotePdf(quoteDetails) &&
    hasCompleteDetails() &&
    hasCompleteMaterials() &&
    hasCompleteCustomers()

  const getPendingCustomers = () =>
    customers.filter(
      (c) =>
        c.customerName.trim() &&
        c.customerMailId.trim() &&
        !customerAlreadyEmailed(c)
    )

  const getAlreadyEmailedCustomers = () =>
    customers.filter(
      (c) =>
        c.customerName.trim() &&
        c.customerMailId.trim() &&
        customerAlreadyEmailed(c)
    )

  const hasPendingCustomersToEmail = () => getPendingCustomers().length > 0

  const syncCustomersFromQuote = (quote) => {
    if (!quote || typeof quote !== 'object') return
    const customerList = Array.isArray(quote.customer)
      ? quote.customer
      : Array.isArray(quote.customers)
        ? quote.customers
        : []
    if (customerList.length === 0) return

    const apiByMail = new Map(
      customerList.map((c) => [
        String(c.customerMailId || '').trim().toLowerCase(),
        c
      ])
    )
    setCustomers((prev) => {
      const next = prev
        .filter((row) => row.customerName.trim() || row.customerMailId.trim())
        .map((row) => {
          const mail = row.customerMailId.trim().toLowerCase()
          const fromApi = apiByMail.get(mail)
          if (!fromApi) return row
          return {
            ...row,
            customerId: fromApi.customerId || row.customerId || '',
            customerName: fromApi.customerName || row.customerName,
            phoneNumber: fromApi.phoneNumber || fromApi.phonenumber || row.phoneNumber || '',
            emailLink: fromApi.emailLink || row.emailLink || ''
          }
        })
      apiByMail.forEach((apiRow, mail) => {
        const exists = next.some(
          (row) => row.customerMailId.trim().toLowerCase() === mail
        )
        if (!exists) {
          next.push({
            customerId: apiRow.customerId || '',
            customerName: apiRow.customerName || '',
            customerMailId: apiRow.customerMailId || '',
            phoneNumber: apiRow.phoneNumber || apiRow.phonenumber || '',
            emailLink: apiRow.emailLink || ''
          })
        }
      })
      return next.length > 0 ? next : [emptyCustomer()]
    })
  }

  const getUserId = () => {
    const userId = localStorage.getItem('id')
    if (!userId) {
      toast.error('User session expired. Please login again.', { autoClose: 2000 })
      setTimeout(() => navigate('/'), 2000)
      return null
    }
    return userId
  }

  const buildQuoteDeliveryTimeForApi = () => {
    const display = String(quoteDetails.quoteDeliveryTimeDisplay || '').trim()
    const deliveryIso = quoteDetails.quoteDeliveryDate
    if (display && deliveryIso && parseDateFromApi(display) === deliveryIso) {
      return display
    }
    return formatDateTimeForApi(deliveryIso, getCurrentTimeHHmm())
  }

  const buildTab1DetailsFields = () => ({
    quoteNumber: quoteDetails.quoteNumber.trim(),
    quoteDate: formatDateForApi(quoteDetails.quoteDate),
    quoteDeliveryTime: buildQuoteDeliveryTimeForApi(),
    quoteSummary: quoteDetails.quoteSummary.trim(),
    quoteCurrency: quoteDetails.quoteCurrency.trim(),
    quoteStatus: DEFAULT_QUOTE_STATUS,
    terms: buildTermsPayload()
  })

  const applyPdfFieldsToQuoteDetails = (pdfFields) => {
    if (!pdfFields?.pdfLink && !pdfFields?.pdfDownloadUrl) return
    setQuoteDetails((prev) => ({
      ...prev,
      ...(pdfFields.pdfLink ? { pdfLink: pdfFields.pdfLink } : {}),
      ...(pdfFields.pdfDownloadUrl ? { pdfDownloadUrl: pdfFields.pdfDownloadUrl } : {})
    }))
  }

  const refreshQuoteById = useCallback(async (options = {}) => {
    const { preserveActiveStatus = false } = options
    if (!quoteId) return null
    try {
      const response = await axios.post(API_URL, {
        action: 'get-sales-quote-by-id',
        id: quoteId
      })
      const responseData = parseApiResponse(response.data)
      const quote = pickQuoteFromResponse(responseData, quoteId)
      if (!isApiSuccess(responseData) || !quote || typeof quote !== 'object') {
        return null
      }
      if (!preserveActiveStatus) {
        setQuoteRecordStatus(quote.status || '')
      }
      setIsMailSent(Boolean(quote.isMailSent))
      applyPdfFieldsToQuoteDetails({
        pdfLink: quote.pdfLink || '',
        pdfDownloadUrl: quote.pdfDownloadUrl || ''
      })
      const ownerAttachments = getSqOwnerListFromQuote(quote)
      if (ownerAttachments.length > 0) {
        setSqOwnerAttachments(ownerAttachments.map(mapSqOwnerFromApi))
      }
      syncCustomersFromQuote(quote)
      if (String(quote.status || '').toLowerCase() === FINAL_STATUS.toLowerCase()) {
        setShowPreviewStep(true)
      }
      return quote
    } catch (error) {
      console.error('Refresh sales quote error:', error)
      return null
    }
  }, [quoteId])

  const applyQuoteFromResponse = (quote) => {
    if (!quote || typeof quote !== 'object') return
    if (quote.quoteNumber) {
      setQuoteDetails((prev) => ({ ...prev, quoteNumber: quote.quoteNumber }))
    }
    if (quote.quoteDeliveryTime) {
      setQuoteDetails((prev) => ({
        ...prev,
        quoteDeliveryTimeDisplay: quote.quoteDeliveryTime
      }))
    }
    if (quote.status) {
      setQuoteRecordStatus(quote.status)
    }
    applyPdfFieldsToQuoteDetails({
      pdfLink: quote.pdfLink || '',
      pdfDownloadUrl: quote.pdfDownloadUrl || ''
    })
    const ownerAttachments = getSqOwnerListFromQuote(quote)
    if (ownerAttachments.length > 0) {
      setSqOwnerAttachments(ownerAttachments.map(mapSqOwnerFromApi))
    }
  }

  const postQuoteRequest = async (payload) => {
    const response = await axios.post(API_URL, payload)
    const responseData = parseApiResponse(response.data)

    if (!isApiSuccess(responseData)) {
      toast.error(responseData?.message || 'Failed to save sales quote', { autoClose: 2000 })
      return { ok: false, responseData }
    }

    return { ok: true, responseData }
  }

  const saveTab1Details = async () => {
    const userId = getUserId()
    if (!userId) return false

    setIsLoading(true)
    try {
      let payload
      if (quoteId) {
        payload = {
          action: 'update-sales-quote',
          id: quoteId,
          ...buildTab1DetailsFields(),
          updatedBy: userId
        }
      } else {
        payload = {
          action: 'create-sales-quote',
          ...buildTab1DetailsFields(),
          createdBy: userId
        }
      }

      const { ok, responseData } = await postQuoteRequest(payload)
      if (!ok) return false

      const newId = quoteId || extractCreatedQuoteId(responseData)
      if (newId && !quoteId) {
        setQuoteId(newId)
      }

      applyQuoteFromResponse(responseData?.data)
      toast.success(responseData?.message || 'Draft saved', { autoClose: 1500 })
      return true
    } catch (error) {
      console.error('Save sales quote details error:', error)
      const errData = error.response?.data ? parseApiResponse(error.response.data) : null
      toast.error(errData?.message || 'An error occurred while saving sales quote', {
        autoClose: 2000
      })
      return false
    } finally {
      setIsLoading(false)
    }
  }

  const saveTab2Materials = async () => {
    const userId = getUserId()
    if (!userId) return false
    if (!quoteId) {
      toast.error('Save quote details first', { autoClose: 2000 })
      return false
    }

    setIsLoading(true)
    try {
      const { ok, responseData } = await postQuoteRequest({
        action: 'update-sales-quote',
        id: quoteId,
        userid: userId,
        ...buildMaterialsSaveFields(materials)
      })
      if (!ok) return false

      applyQuoteFromResponse(responseData?.data)
      if (Array.isArray(responseData?.data?.materials)) {
        setMaterials(responseData.data.materials.map(mapMaterialFromApi))
      }
      toast.success('Materials saved', { autoClose: 1500 })
      return true
    } catch (error) {
      console.error('Save materials error:', error)
      const errData = error.response?.data ? parseApiResponse(error.response.data) : null
      toast.error(errData?.message || 'An error occurred while saving materials', { autoClose: 2000 })
      return false
    } finally {
      setIsLoading(false)
    }
  }

  const saveTab3Customers = async () => {
    const userId = getUserId()
    if (!userId) return false
    if (!quoteId) {
      toast.error('Save quote details first', { autoClose: 2000 })
      return false
    }

    const { ok, responseData } = await postQuoteRequest({
      action: 'update-sales-quote',
      id: quoteId,
      customer: buildCustomersPayload(),
      updatedBy: userId
    })
    if (ok) {
      applyQuoteFromResponse(responseData?.data)
    }
    return ok
  }

  const persistPendingAttachments = async () => {
    if (pendingAttachmentFiles.length === 0) return true

    const userId = getUserId()
    if (!userId) return false

    const quoteNumber = quoteDetails.quoteNumber.trim()
    if (!quoteNumber) {
      toast.error('Quote number is required for attachments', { autoClose: 2000 })
      return false
    }

    if (!quoteId) {
      toast.error('Save quote details first', { autoClose: 2000 })
      return false
    }

    setIsUploadingAttachments(true)
    try {
      const fileNames = pendingAttachmentFiles.map((f) => f.name)
      const contentTypes = pendingAttachmentFiles.map(
        (f) => f.type || 'application/octet-stream'
      )

      const response = await axios.post(API_URL, {
        action: 'get-sq-owner-attachment-upload-presigned-url',
        quoteNumber,
        fileNames,
        contentTypes,
        expiresIn: 7200
      })
      const responseData = parseApiResponse(response.data)

      if (!isApiSuccess(responseData) || !Array.isArray(responseData.files)) {
        toast.error(responseData?.message || 'Failed to get upload URLs', { autoClose: 2000 })
        return false
      }

      const presignedFiles = responseData.files
      if (presignedFiles.length !== pendingAttachmentFiles.length) {
        toast.error('Upload URL count mismatch', { autoClose: 2000 })
        return false
      }

      await Promise.all(
        presignedFiles.map((fileMeta, idx) => {
          const file = pendingAttachmentFiles[idx]
          const contentType =
            fileMeta.contentType || file.type || 'application/octet-stream'
          return axios.put(fileMeta.uploadUrl, file, {
            headers: { 'Content-Type': contentType }
          })
        })
      )

      const s3Keys = presignedFiles.map((f) => f.s3Key)
      const originalFileNames = presignedFiles.map(
        (f) => f.originalFileName || f.fileName
      )
      const savedContentTypes = presignedFiles.map(
        (f) => f.contentType || 'application/octet-stream'
      )

      const updatePayload = {
        action: 'update-sales-quote',
        id: quoteId,
        updatedBy: userId,
        s3Keys,
        originalFileNames,
        contentTypes: savedContentTypes
      }
     if (sqOwnerAttachments.length > 0) {
       updatePayload.appendQuoteOwner = true   
       }

      const { ok, responseData: updateData } = await postQuoteRequest(updatePayload)
      if (!ok) return false

      const updatedQuote = updateData?.data
      const ownerAttachments = getSqOwnerListFromQuote(updatedQuote)
      if (ownerAttachments.length > 0) {
        setSqOwnerAttachments(ownerAttachments.map(mapSqOwnerFromApi))
      } else {
        const newEntries = presignedFiles.map((f, idx) => ({
          s3Key: f.s3Key,
          originalFileName: originalFileNames[idx],
          contentType: savedContentTypes[idx],
          uploadedAt: new Date().toISOString(),
          url: ''
        }))
        setSqOwnerAttachments((prev) => [...prev, ...newEntries])
      }

      setPendingAttachmentFiles([])
      return true
    } catch (error) {
      console.error('Upload attachments error:', error)
      const errData = error.response?.data ? parseApiResponse(error.response.data) : null
      toast.error(errData?.message || 'Failed to upload attachments', { autoClose: 2000 })
      return false
    } finally {
      setIsUploadingAttachments(false)
    }
  }

  const saveTabAttachments = async () => {
    const userId = getUserId()
    if (!userId) return false
    if (!quoteId) {
      toast.error('Save quote details first', { autoClose: 2000 })
      return false
    }

    setIsLoading(true)
    try {
      if (pendingAttachmentFiles.length > 0) {
        const uploaded = await persistPendingAttachments()
        if (!uploaded) return false
        toast.success('Attachments saved', { autoClose: 1500 })
      }
      return true
    } finally {
      setIsLoading(false)
    }
  }

  const handleAttachmentFileSelect = (e) => {
    const files = Array.from(e.target.files || [])
    e.target.value = ''
    if (files.length === 0) return

    const totalCount =
      sqOwnerAttachments.length + pendingAttachmentFiles.length + files.length
    if (totalCount > MAX_SQ_OWNER_ATTACHMENTS) {
      toast.error(`Maximum ${MAX_SQ_OWNER_ATTACHMENTS} attachments allowed`, {
        autoClose: 2000
      })
      return
    }

    setPendingAttachmentFiles((prev) => [...prev, ...files])
  }

  const removePendingAttachment = (index) => {
    setPendingAttachmentFiles((prev) => prev.filter((_, i) => i !== index))
  }

  const downloadSqOwnerAttachment = async (s3Key) => {
    if (!s3Key) return
    try {
      const response = await axios.post(API_URL, {
        action: 'get-sq-owner-attachment-download-presigned-url',
        s3Keys: [s3Key],
        expiresIn: 3600
      })
      const responseData = parseApiResponse(response.data)
      if (!isApiSuccess(responseData)) {
        toast.error(responseData?.message || 'Failed to get download URL', { autoClose: 2000 })
        return
      }
      const downloadUrl =
        responseData.files?.[0]?.downloadUrl || responseData.downloadUrl
      if (!downloadUrl) {
        toast.error('Download URL not available', { autoClose: 2000 })
        return
      }
      window.open(downloadUrl, '_blank', 'noopener,noreferrer')
    } catch (error) {
      console.error('Download attachment error:', error)
      toast.error('Failed to download attachment', { autoClose: 2000 })
    }
  }

  const removeSqOwnerAttachment = async (s3Key) => {
    const userId = getUserId()
    if (!userId || !quoteId) return

    setIsUploadingAttachments(true)
    try {
      const remaining = sqOwnerAttachments.filter((a) => a.s3Key !== s3Key)
      const { ok, responseData } = await postQuoteRequest({
        action: 'update-sales-quote',
        id: quoteId,
        updatedBy: userId,
        quoteOwner: remaining.map((a) => ({
          s3Key: a.s3Key,
          originalFileName: a.originalFileName,
          contentType: a.contentType
        }))
      })
      if (!ok) return
      const ownerAttachments = getSqOwnerListFromQuote(responseData?.data)
      if (ownerAttachments.length > 0) {
        setSqOwnerAttachments(ownerAttachments.map(mapSqOwnerFromApi))
      } else {
        setSqOwnerAttachments(remaining)
      }
      toast.success('Attachment removed', { autoClose: 1500 })
    } finally {
      setIsUploadingAttachments(false)
    }
  }

  const activateSalesQuote = async () => {
    const userId = getUserId()
    if (!userId) return false
    if (!quoteId) {
      toast.error('Save quote details first', { autoClose: 2000 })
      return false
    }

    setIsLoading(true)
    try {
      if (pendingAttachmentFiles.length > 0) {
        const uploaded = await persistPendingAttachments()
        if (!uploaded) return false
      }

      const customersSaved = await saveTab3Customers()
      if (!customersSaved) return false

      const { ok, responseData } = await postQuoteRequest({
        action: 'update-sales-quote',
        id: quoteId,
        status: FINAL_STATUS,
        ...buildMaterialsSaveFields(materials),
        customer: buildCustomersPayload(),
        updatedBy: userId
      })
      if (!ok) return false

      setQuoteRecordStatus(FINAL_STATUS)
      setShowPreviewStep(true)
      applyQuoteFromResponse(responseData?.data)

      const pdfFields = extractPdfFieldsFromResponse(responseData)
      applyPdfFieldsToQuoteDetails(pdfFields)
      if (!pdfFields.pdfLink) {
        await refreshQuoteById({ preserveActiveStatus: true })
      }

      setActiveTab('preview')
      toast.success(
        responseData?.message || 'Sales quote activated. Review PDF and send email.',
        { autoClose: 2000 }
      )
      window.scrollTo({ top: 0, behavior: 'smooth' })
      return true
    } catch (error) {
      console.error('Activate sales quote error:', error)
      const errData = error.response?.data ? parseApiResponse(error.response.data) : null
      toast.error(errData?.message || 'An error occurred while activating sales quote', {
        autoClose: 2000
      })
      return false
    } finally {
      setIsLoading(false)
    }
  }

  const updateActiveSalesQuote = async () => {
    const userId = getUserId()
    if (!userId) return false
    if (!quoteId) {
      toast.error('Save quote details first', { autoClose: 2000 })
      return false
    }

    setIsLoading(true)
    try {
      if (pendingAttachmentFiles.length > 0) {
        const uploaded = await persistPendingAttachments()
        if (!uploaded) return false
      }

      const { ok, responseData } = await postQuoteRequest({
        action: 'update-sales-quote',
        id: quoteId,
        updatedBy: userId,
        status: FINAL_STATUS,
        ...buildTab1DetailsFields(),
        ...buildMaterialsSaveFields(materials),
        customer: buildCustomersPayload()
      })
      if (!ok) return false

      applyQuoteFromResponse(responseData?.data)
      syncCustomersFromQuote(responseData?.data)

      const pdfFields = extractPdfFieldsFromResponse(responseData)
      applyPdfFieldsToQuoteDetails(pdfFields)
      if (!pdfFields.pdfLink && !pdfFields.pdfDownloadUrl) {
        await refreshQuoteById({ preserveActiveStatus: true })
      }

      setActiveTab('preview')

      const pendingCount = getPendingCustomers().length
      let successMessage =
        responseData?.message || 'Sales quote updated successfully'
      if (pendingCount > 0) {
        successMessage = `${successMessage} Review PDF and send email to new customer(s).`
      }
      toast.success(successMessage, { autoClose: 2500 })
      window.scrollTo({ top: 0, behavior: 'smooth' })
      return true
    } catch (error) {
      console.error('Update sales quote error:', error)
      const errData = error.response?.data ? parseApiResponse(error.response.data) : null
      toast.error(errData?.message || 'An error occurred while updating sales quote', {
        autoClose: 2000
      })
      return false
    } finally {
      setIsLoading(false)
    }
  }

const handleBack = async (e) => {
  e.preventDefault();

  switch (activeTab) {
    case "details":
      await saveTab1Details();
      break;

    case "materials":
      await saveTab2Materials();
      break;

    case "attachments":
      await saveTabAttachments();
      break;

    case "customer":
      await saveTab3Customers();
      break;

    default:
      break;
  }

  navigate("/salesQuotes");
};

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!validateDetails() || !validateMaterials() || !validateCustomerTab()) {
      return
    }

    if (isQuoteActive) {
      await updateActiveSalesQuote()
    } else {
      await activateSalesQuote()
    }
  }

  const sendSalesQuoteEmail = async () => {
    const userId = getUserId()
    if (!userId) return

    if (!quoteId) {
      toast.error('Save quote details first', { autoClose: 2000 })
      return
    }
    if (!isQuoteActive) {
      toast.error('Activate sales quote before sending email', { autoClose: 2000 })
      return
    }
    if (!validateDetails() || !validateMaterials() || !validateCustomerTab()) {
      return
    }
    if (!hasSalesQuotePdf(quoteDetails)) {
      toast.error('PDF preview is not available yet. Please try again shortly.', {
        autoClose: 2000
      })
      return
    }
    if (!hasPendingCustomersToEmail()) {
      toast.info('All customers have already been emailed.', { autoClose: 2500 })
      return
    }

    const ccEmails = buildCcEmails(userEmail, additionalCcEmails)
    const invalidCc = ccEmails.find((email) => !isValidEmailAddress(email))
    if (invalidCc) {
      toast.error(`Invalid CC email: ${invalidCc}`, { autoClose: 2000 })
      return
    }
    if (ccEmails.length === 0) {
      toast.error('Could not get your email for CC. Add CC emails or check your profile.', {
        autoClose: 2000
      })
      return
    }

    setIsSendingEmail(true)
    try {
      const response = await axios.post(API_URL, {
        action: 'send-sales-quote-email',
        id: quoteId,
        userid: userId,
        ccEmails
      })
      const responseData = parseApiResponse(response.data)

      if (!isApiSuccess(responseData)) {
        toast.error(responseData?.message || 'Failed to send sales quote email', {
          autoClose: 2000
        })
        return
      }

      const data = responseData?.data || {}
      const results = Array.isArray(data.results) ? data.results : []
      const skipped = Array.isArray(data.skippedAlreadyEmailed)
        ? data.skippedAlreadyEmailed
        : []
      const totalRecipients = Number(data.totalRecipients)
      const sentCount = Number.isFinite(totalRecipients) ? totalRecipients : results.length

      if (sentCount === 0) {
        const skippedMsg =
          skipped.length > 0
            ? `Already emailed: ${skipped.join(', ')}`
            : responseData?.message || 'No new customer emails to send'
        toast.info(skippedMsg, { autoClose: 3000 })
        await refreshQuoteById()
        return
      }

      const resultByMail = new Map(
        results.map((row) => [String(row.to || row.customerMailId || '').trim().toLowerCase(), row])
      )
      setCustomers((prev) =>
        prev.map((customer) => {
          const mail = customer.customerMailId.trim().toLowerCase()
          const result = resultByMail.get(mail)
          const link = result?.quoteLink || result?.emailLink || result?.customerLink
          if (result?.success !== false && link) {
            return { ...customer, emailLink: link }
          }
          return customer
        })
      )

      setIsMailSent(true)
      let successMessage = responseData?.message || 'Sales quote email sent successfully'
      if (skipped.length > 0) {
        successMessage += ` Skipped (already emailed): ${skipped.join(', ')}`
      }
      toast.success(successMessage, { autoClose: 3000 })
      await refreshQuoteById()
    } catch (error) {
      console.error('Send sales quote email error:', error)
      const errData = error.response?.data ? parseApiResponse(error.response.data) : null
      toast.error(errData?.message || 'An error occurred while sending email', {
        autoClose: 2000
      })
    } finally {
      setIsSendingEmail(false)
    }
  }

const TAB_ORDER = ['details', 'materials', 'attachments', 'customer', 'preview']

const isForwardMove = (fromTab, toTab) =>
  TAB_ORDER.indexOf(toTab) > TAB_ORDER.indexOf(fromTab)

const goToTab = async (nextTab) => {
  if (nextTab === activeTab) return

  if (nextTab === 'preview' && !canAccessPreview) return

  const forward = isForwardMove(activeTab, nextTab)

  if (forward) {
    switch (activeTab) {
      case 'details':
        if (!validateDetails()) return
        if (!(await saveTab1Details())) return
        break

      case 'materials':
        if (!validateMaterials()) return
        if (!(await saveTab2Materials())) return
        break

      case 'attachments':
        if (!(await saveTabAttachments())) return
        break

      case 'customer':
        if (!validateCustomerTab()) return
        if (!(await saveTab3Customers())) return
        break

      default:
        break
    }
  }

  setActiveTab(nextTab)
}

  const tabClass = (tab) => (activeTab === tab ? 'nav-link active' : 'nav-link')

  return (
    <div>
      <div className="content-header">
        <div className="container-fluid">
          <div className="row mb-2 align-items-center">
            <div className="col-6 col-sm-6">
              <h1 className="m-0 txt_lft">
                {isEditMode ? 'Edit Sales Quote' : 'Create Sales Quote'}
              </h1>
            </div>
            <div className="col-6 col-sm-6">
              <ol className="breadcrumb justify-content-end">
                <li className="breadcrumb-item">
                <a href="/salesQuotes" onClick={handleBack}>
                <i className="fa-solid fa-arrow-left-long" /> Back
                </a>
                </li>
              </ol>
            </div>
          </div>
        </div>
      </div>

      <div className="content">
        <div className="container-fluid">
          <div className="row">
            <div className="col-lg-12">
              <div className="card card-primary card-outline card-tabs">
                <div className="card-header p-0 pt-1 border-bottom-0">
                  <ul className="nav nav-tabs" role="tablist">
                    <li className="nav-item">
                      <button
                        type="button"
                        className={tabClass('details')}
                        onClick={() => goToTab('details')}
                      >
                        Quote Details
                      </button>
                    </li>
                    <li className="nav-item">
                      <button
                        type="button"
                        className={tabClass('materials')}
                        onClick={() => goToTab('materials')}
                      >
                        Materials
                      </button>
                    </li>
                    <li className="nav-item">
                      <button
                        type="button"
                        className={tabClass('attachments')}
                        onClick={() => goToTab('attachments')}
                      >
                        Attachments
                      </button>
                    </li>
                    <li className="nav-item">
                      <button
                        type="button"
                        className={tabClass('customer')}
                        onClick={() => goToTab('customer')}
                      >
                        Customer Mail ID
                      </button>
                    </li>
                    <li className="nav-item">
                      <button
                        type="button"
                        className={tabClass('preview')}
                        onClick={() => canAccessPreview && goToTab('preview')}
                        disabled={!canAccessPreview}
                        style={!canAccessPreview ? { opacity: 0.6, cursor: 'not-allowed' } : undefined}
                      >
                        Preview & Email
                      </button>
                    </li>
                  </ul>
                </div>

                <form onSubmit={handleSubmit}>
                  {isLoadingQuote ? (
                    <div className="card-body text-center py-5">
                      <PulseLoader color="#1199B2" size={10} />
                    </div>
                  ) : (
                  <div className="card-body">
                    {activeTab === 'details' && (
                      <div className="row">
                        <div className="col-md-3">
                          <div className="form-group">
                            <label className={fieldErrors.quoteNumber ? 'text-danger' : ''}>
                              Quotation Number
                            </label>
                            <div style={{ position: 'relative' }}>
                              <input
                                type="text"
                                name="quoteNumber"
                                className={fieldControlClass('quoteNumber')}
                                placeholder={
                                  isFetchingQuoteNumber ? 'Loading...' : 'SQ-ITS-0001'
                                }
                                value={quoteDetails.quoteNumber}
                                readOnly
                                disabled={isFetchingQuoteNumber}
                                style={{ backgroundColor: '#e9ecef', cursor: 'not-allowed' }}
                                required
                              />
                              {isFetchingQuoteNumber && (
                                <div
                                  style={{
                                    position: 'absolute',
                                    right: '10px',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    pointerEvents: 'none'
                                  }}
                                >
                                  <PulseLoader color="#1199B2" size={6} />
                                </div>
                              )}
                            </div>
                            {fieldErrors.quoteNumber && (
                              <small className="form-text text-danger">
                                {fieldErrors.quoteNumber}
                              </small>
                            )}
                          </div>
                        </div>
                        <div className="col-md-3">
                          <div className="form-group">
                            <label className={fieldErrors.quoteDate ? 'text-danger' : ''}>
                              Quotation Date
                            </label>
                            <input
                              type="date"
                              name="quoteDate"
                              className={fieldControlClass('quoteDate')}
                              value={quoteDetails.quoteDate}
                              onChange={handleDetailsChange}
                              required
                            />
                            {fieldErrors.quoteDate && (
                              <small className="form-text text-danger">
                                {fieldErrors.quoteDate}
                              </small>
                            )}
                          </div>
                        </div>
                        <div className="col-md-3">
                          <div className="form-group">
                            <label
                              className={fieldErrors.quoteDeliveryDate ? 'text-danger' : ''}
                            >
                              Quotation Validity Date
                            </label>
                            <input
                              type="date"
                              name="quoteDeliveryDate"
                              className={fieldControlClass('quoteDeliveryDate')}
                              value={quoteDetails.quoteDeliveryDate}
                              onChange={handleDetailsChange}
                              min={quoteDetails.quoteDate || undefined}
                              required
                            />
                            {fieldErrors.quoteDeliveryDate && (
                              <small className="form-text text-danger">
                                {fieldErrors.quoteDeliveryDate}
                              </small>
                            )}
                          </div>
                        </div>
                        <div className="col-md-3">
                          <div className="form-group">
                            <label className={fieldErrors.quoteCurrency ? 'text-danger' : ''}>
                              Currency
                            </label>
                            <select
                              name="quoteCurrency"
                              className={fieldControlClass('quoteCurrency')}
                              value={quoteDetails.quoteCurrency}
                              onChange={handleDetailsChange}
                            >
                              {CURRENCY_OPTIONS.map((c) => (
                                <option key={c} value={c}>
                                  {c}
                                </option>
                              ))}
                            </select>
                            {fieldErrors.quoteCurrency && (
                              <small className="form-text text-danger">
                                {fieldErrors.quoteCurrency}
                              </small>
                            )}
                          </div>
                        </div>

                        <div className="col-md-12">
                          <div className="form-group">
                            <label className={fieldErrors.quoteSummary ? 'text-danger' : ''}>
                              Quotation Summary
                            </label>
                            <textarea
                              name="quoteSummary"
                              className={fieldControlClass('quoteSummary')}
                              rows={4}
                              placeholder="Summary"
                              value={quoteDetails.quoteSummary}
                              onChange={handleDetailsChange}
                              required
                            />
                            {fieldErrors.quoteSummary && (
                              <small className="form-text text-danger">
                                {fieldErrors.quoteSummary}
                              </small>
                            )}
                          </div>
                        </div>

                        <div className="col-md-12 mt-3">
                          <label className="mb-2">
                            Terms
                          </label>
                          <div style={{ overflowX: 'auto' }}>
                            {terms.length === 0 ? (
                              <div
                                className="text-center py-4 mb-0"
                                style={{
                                  border: '1px dashed #dee2e6',
                                  borderRadius: '4px'
                                }}
                              >
                                <p className="text-muted mb-2 small">No terms added.</p>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-blue"
                                  onClick={() => setTerms(getDefaultTerms())}
                                >
                                  <i className="fa-solid fa-plus mr-1" />
                                  Add Terms
                                </button>
                              </div>
                            ) : (
                            <table
                              className="table table-bordered table-hover mb-0"
                              style={{ tableLayout: 'fixed', width: '100%' }}
                            >
                              <colgroup>
                                <col style={{ width: '56px' }} />
                                <col style={{ width: '28%' }} />
                                <col style={{ width: 'calc(72% - 146px)' }} />
                                <col style={{ width: '90px' }} />
                              </colgroup>
                              <thead>
                                <tr style={{ backgroundColor: '#003366', color: '#fff' }}>
                                  <th style={{ fontSize: '12px', whiteSpace: 'nowrap' }}>S.No</th>
                                  <th style={{ fontSize: '12px' }}>Quote Term</th>
                                  <th style={{ fontSize: '12px' }}>Quote Response</th>
                                  <th style={{ fontSize: '12px' }}>Action</th>
                                </tr>
                              </thead>
                              <tbody>
                                {terms.map((row, index) => (
                                  <tr key={index}>
                                    <td style={{ verticalAlign: 'top' }}>{index + 1}</td>
                                    <td style={{ verticalAlign: 'top' }}>
                                      <input
                                        type="text"
                                        className="form-control form-control-sm"
                                        value={row.quoteTerm}
                                        onChange={(e) =>
                                          updateTerm(index, 'quoteTerm', e.target.value)
                                        }
                                        style={{ width: '100%' }}
                                      />
                                    </td>
                                    <td style={{ verticalAlign: 'top' }}>
                                      <textarea
                                        className={fieldControlClass(
                                          getTermFieldErrorKey(row.quoteTerm),
                                          'form-control-sm'
                                        )}
                                        rows={2}
                                        value={row.quoteResponse}
                                        onChange={(e) =>
                                          updateTerm(index, 'quoteResponse', e.target.value)
                                        }
                                        placeholder={
                                          isMandatoryQuoteTerm(row.quoteTerm)
                                            ? 'Required'
                                            : ''
                                        }
                                        required={isMandatoryQuoteTerm(row.quoteTerm)}
                                        style={{
                                          width: '100%',
                                          resize: 'vertical',
                                          whiteSpace: 'pre-wrap',
                                          wordBreak: 'break-word'
                                        }}
                                      />
                                      {fieldErrors[getTermFieldErrorKey(row.quoteTerm)] && (
                                        <small className="form-text text-danger d-block mt-1">
                                          {fieldErrors[getTermFieldErrorKey(row.quoteTerm)]}
                                        </small>
                                      )}
                                    </td>
                                    <td
                                      className="text-center"
                                      style={{ whiteSpace: 'nowrap', verticalAlign: 'top' }}
                                    >
                                      <div className="btn-group" role="group">
                                        <button
                                          type="button"
                                          className="btn btn-sm btn-success"
                                          onClick={() => addTermRowAfter(index)}
                                          style={{ padding: '3px 8px' }}
                                        >
                                          <i className="fa-solid fa-plus" aria-hidden="true" />
                                        </button>
                                        <button
                                          type="button"
                                          className="btn btn-sm btn-danger"
                                          onClick={() => removeTermRow(index)}
                                          style={{ padding: '3px 8px' }}
                                        >
                                          <i className="fa-solid fa-trash-can" aria-hidden="true" />
                                        </button>
                                      </div>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {activeTab === 'materials' && (
                      <div>
                        <div className="row align-items-end mb-3">
                          <div className="col-md-6">
                            <div className="form-group mb-0">
                              <label>Search by PR Number</label>
                              <div className="input-group">
                                <input
                                  type="text"
                                  className="form-control"
                                  placeholder="Enter PR number..."
                                  value={prNoSearch}
                                  onChange={handlePrSearchChange}
                                  onKeyPress={handlePrSearchKeyPress}
                                />
                                <div className="input-group-append">
                                  <button
                                    type="button"
                                    className="btn btn-blue"
                                    onClick={handlePrNoSearch}
                                    disabled={prSearchLoading}
                                  >
                                    {prSearchLoading ? (
                                      <PulseLoader color="#fff" size={6} />
                                    ) : (
                                      <i className="fa-solid fa-search" />
                                    )}
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        {prSearchResults.length > 0 && (
                          <div
                            className="mb-3"
                            style={{
                              border: '1px solid #dee2e6',
                              borderRadius: '6px',
                              overflow: 'hidden'
                            }}
                          >
                            <div
                              className="d-flex justify-content-between align-items-center flex-wrap"
                              style={{
                                padding: '8px 12px',
                                backgroundColor: '#f8f9fa',
                                gap: '8px',
                                cursor: 'pointer'
                              }}
                              onClick={() => setPrSearchResultsOpen((open) => !open)}
                              role="button"
                              tabIndex={0}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault()
                                  setPrSearchResultsOpen((open) => !open)
                                }
                              }}
                            >
                              <span
                                className="d-flex align-items-center"
                                style={{ fontWeight: 600, fontSize: '13px', gap: '8px' }}
                              >
                                <i
                                  className={`fa-solid ${
                                    prSearchResultsOpen ? 'fa-chevron-up' : 'fa-chevron-down'
                                  }`}
                                  style={{ width: '14px', color: '#003366' }}
                                  aria-hidden
                                />
                                PR search results — {countPendingPrSelection()} to add,{' '}
                                {countAddedInCurrentResults()} already added
                              </span>
                              {prSearchResultsOpen && (
                                <button
                                  type="button"
                                  className="btn btn-sm btn-blue"
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    applySelectedProcurements()
                                  }}
                                  disabled={countPendingPrSelection() === 0}
                                >
                                  Add
                                </button>
                              )}
                            </div>
                            {prSearchResultsOpen && (
                              <div style={{ maxHeight: '220px', overflow: 'auto' }}>
                                <table
                                  className="table table-sm table-bordered mb-0"
                                  style={{ minWidth: '980px' }}
                                >
                                  <thead>
                                    <tr style={{ backgroundColor: '#003366', color: '#fff' }}>
                                      <th style={{ fontSize: '11px', width: '36px' }}>
                                        <input
                                          type="checkbox"
                                          checked={
                                            getSelectablePrKeys().length > 0 &&
                                            getSelectablePrKeys().every((key) =>
                                              selectedPrKeys.has(key)
                                            )
                                          }
                                          onChange={toggleSelectAllPrResults}
                                          title="Select all not yet added"
                                        />
                                      </th>
                                      <th style={{ fontSize: '11px' }}>PR No</th>
                                      <th style={{ fontSize: '11px' }}>Material Code</th>
                                      <th style={{ fontSize: '11px' }}>Material Description</th>
                                      <th style={{ fontSize: '11px' }}>HSN</th>
                                      <th style={{ fontSize: '11px' }}>UOM</th>
                                      <th style={{ fontSize: '11px' }}>Quantity</th>
                                      <th style={{ fontSize: '11px', minWidth: '140px' }}>
                                        Quotation Status
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {prSearchResults.map((item, idx) => {
                                      const key = getProcurementKey(item, idx)
                                      const alreadyAdded = isProcurementInMaterials(materials, item)
                                      const isSelected = selectedPrKeys.has(key)
                                      const quotationStatus = getProcurementQuotationStatus(item)
                                      return (
                                        <tr
                                          key={key}
                                          style={{
                                            backgroundColor: alreadyAdded
                                              ? '#e8f5e9'
                                              : isSelected
                                                ? '#e8f4f8'
                                                : undefined
                                          }}
                                        >
                                          <td className="text-center">
                                            <input
                                              type="checkbox"
                                              checked={isSelected}
                                              onChange={() => togglePrSelection(key)}
                                              disabled={alreadyAdded}
                                              title={
                                                alreadyAdded
                                                  ? 'Already added to materials'
                                                  : ''
                                              }
                                            />
                                          </td>
                                          <td style={{ fontSize: '12px' }}>
                                            {item.prNo || item.prNumber || '-'}
                                          </td>
                                          <td style={{ fontSize: '12px' }}>
                                            {item.materialCode || '-'}
                                          </td>
                                          <td
                                            style={{
                                              fontSize: '12px',
                                              maxWidth: '180px',
                                              whiteSpace: 'normal',
                                              wordBreak: 'break-word'
                                            }}
                                          >
                                            {item.materialDescription || '-'}
                                          </td>
                                          <td style={{ fontSize: '12px' }}>
                                            {item.hsnCode || item.hsn || '-'}
                                          </td>
                                          <td style={{ fontSize: '12px' }}>{item.uom || '-'}</td>
                                          <td style={{ fontSize: '12px' }}>
                                            {item.quantity ?? '-'}
                                            {alreadyAdded && (
                                              <span
                                                className="badge badge-success ml-1"
                                                style={{ fontSize: '10px' }}
                                              >
                                                Added
                                              </span>
                                            )}
                                          </td>
                                          <td style={{ fontSize: '12px' }}>
                                            {quotationStatus || '-'}
                                          </td>
                                        </tr>
                                      )
                                    })}
                                  </tbody>
                                </table>
                              </div>
                            )}
                          </div>
                        )}

                        <div style={{ overflowX: "auto" }}>
                            <table
                              className="table table-bordered table-hover mb-3"
                              style={{ minWidth: "1740px" }}
                            >
                              <thead>
                                <tr
                                  style={{
                                    backgroundColor: "#003366",
                                    color: "#fff",
                                  }}
                                >
                                  <th
                                    style={{ fontSize: "12px", width: "56px" }}
                                  >
                                    S.No
                                  </th>
                                  <th
                                    style={{
                                      fontSize: "12px",
                                      minWidth: "120px",
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    PR Number
                                  </th>
                                  <th
                                    style={{
                                      fontSize: "12px",
                                      minWidth: "130px",
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    Material Code
                                  </th>
                                  <th
                                    style={{
                                      fontSize: "12px",
                                      minWidth: "220px",
                                    }}
                                  >
                                    Material Description
                                  </th>
                                  <th
                                    style={{
                                      fontSize: "12px",
                                      minWidth: "110px",
                                    }}
                                  >
                                    HSN
                                  </th>
                                  <th
                                    style={{
                                      fontSize: "12px",
                                      minWidth: "100px",
                                    }}
                                  >
                                    UOM
                                  </th>
                                  <th
                                    style={{
                                      fontSize: "12px",
                                      minWidth: "90px",
                                    }}
                                  >
                                    Quantity
                                  </th>
                                  <th
                                    style={{
                                      fontSize: "12px",
                                      minWidth: "100px",
                                    }}
                                  >
                                    Basic Price
                                  </th>
                                  <th
                                    style={{
                                      fontSize: "12px",
                                      minWidth: "90px",
                                    }}
                                  >
                                    GST %
                                  </th>
                                  <th
                                    style={{
                                      fontSize: "12px",
                                      minWidth: "100px",
                                    }}
                                  >
                                    GST Value
                                  </th>
                                  <th
                                    style={{
                                      fontSize: "12px",
                                      minWidth: "100px",
                                    }}
                                  >
                                    Total Value
                                  </th>
                                  {/* <th style={{ fontSize: '12px', minWidth: '100px' }}>Quote Price</th> */}
                                  <th
                                    style={{
                                      fontSize: "12px",
                                      minWidth: "180px",
                                    }}
                                  >
                                    Technical Remarks
                                  </th>
                                  <th
                                    style={{ fontSize: "12px", width: "90px" }}
                                  >
                                    Action
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {materials.map((row, index) => {
                                  const rowPricing = calcMaterialPricing(
                                    row.basicPrice,
                                    row.quantity,
                                    row.gst,
                                  );
                                  return (
                                    <tr
                                      key={index}
                                      onClick={() =>
                                        setActiveMaterialRowIndex(index)
                                      }
                                      style={{
                                        cursor: "pointer",
                                        backgroundColor:
                                          activeMaterialRowIndex === index
                                            ? "#e8f4f8"
                                            : undefined,
                                      }}
                                    >
                                      <td>{index + 1}</td>
                                      <td style={{ minWidth: "120px" }}>
                                        <input
                                          type="text"
                                          className="form-control form-control-sm"
                                          value={row.prNumber || ""}
                                          onChange={(e) =>
                                            updateMaterial(
                                              index,
                                              "prNumber",
                                              e.target.value,
                                            )
                                          }
                                          onClick={(e) => e.stopPropagation()}
                                          placeholder="PR No"
                                          style={{ minWidth: "110px" }}
                                        />
                                      </td>
                                      <td style={{ minWidth: "130px" }}>
                                        <div
                                          style={{
                                            position: "relative",
                                            width: "100%",
                                            minWidth: "120px",
                                          }}
                                        >
                                          <input
                                            ref={(el) =>
                                              (materialCodeInputRefs.current[
                                                index
                                              ] = el)
                                            }
                                            type="text"
                                            className="form-control form-control-sm"
                                            placeholder={
                                              isMaterialCatalogLoading
                                                ? "Loading materials..."
                                                : "Search material..."
                                            }
                                            value={
                                              materialCodeSearchTerms[index] ??
                                              row.materialCode
                                            }
                                            onChange={(e) =>
                                              handleMaterialCodeSearchChange(
                                                index,
                                                e.target.value,
                                              )
                                            }
                                            onKeyDown={(e) =>
                                              handleMaterialCodeKeyDown(
                                                index,
                                                e,
                                              )
                                            }
                                            onFocus={() =>
                                              openMaterialCodeDropdown(index)
                                            }
                                            onBlur={() =>
                                              setTimeout(
                                                () =>
                                                  closeMaterialCodeDropdown(
                                                    index,
                                                  ),
                                                200,
                                              )
                                            }
                                            onClick={(e) => e.stopPropagation()}
                                            autoComplete="off"
                                            disabled={isMaterialCatalogLoading}
                                          
                                          />
                                          {isMaterialCatalogLoading && (
                                            <div
                                              style={{
                                                position: "absolute",
                                                right: "8px",
                                                top: "50%",
                                                transform: "translateY(-50%)",
                                                pointerEvents: "none",
                                              }}
                                            >
                                              <PulseLoader
                                                color="#1199B2"
                                                size={4}
                                              />
                                            </div>
                                          )}
                                        </div>
                                      </td>
                                    <td style={{ verticalAlign: "top", minWidth: "220px" }}>
                                      <textarea
                                           ref={(el) => (materialDescInputRefs.current[index] = el)}
                                            className="form-control form-control-sm"
                                          rows={2}
                                           placeholder={
                                          isMaterialCatalogLoading ? "Loading materials..." : "Search material description..."
                                          }
                                            value={row.materialDescription}
                                            onChange={(e) => handleMaterialDescSearchChange(index, e.target.value)}
                                            onKeyDown={(e) => handleMaterialDescKeyDown(index, e)}
                                            onFocus={() => openMaterialDescDropdown(index)}
                                            onBlur={() => setTimeout(() => closeMaterialDescDropdown(index), 200)}
                                            onClick={(e) => e.stopPropagation()}
                                            disabled={isMaterialCatalogLoading}
                                                style={{
                                                width: "100%",
                                                minWidth: "200px",
                                                resize: "vertical",
                                                whiteSpace: "pre-wrap",
                                                wordBreak: "break-word",
                                            }}
                                        />
                                     </td>
                                      <td style={{ minWidth: "110px" }}>
                                        <input
                                          type="text"
                                          className="form-control form-control-sm"
                                          value={row.hsn}
                                          onChange={(e) =>
                                            updateMaterial(
                                              index,
                                              "hsn",
                                              e.target.value,
                                            )
                                          }
                                          onClick={(e) => e.stopPropagation()}
                                          style={{ minWidth: "100px" }}
                                        />
                                      </td>
                                      <td style={{ minWidth: "100px" }}>
                                        <input
                                          type="text"
                                          className="form-control form-control-sm"
                                          value={row.uom}
                                          onChange={(e) =>
                                            updateMaterial(
                                              index,
                                              "uom",
                                              e.target.value,
                                            )
                                          }
                                          onClick={(e) => e.stopPropagation()}
                                          style={{ minWidth: "90px" }}
                                        />
                                      </td>
                                      <td>
                                        <input
                                          type="text"
                                          className="form-control form-control-sm"
                                          value={row.quantity}
                                          onChange={(e) =>
                                            handleQuantityChange(
                                              index,
                                              e.target.value,
                                            )
                                          }
                                          onClick={(e) => e.stopPropagation()}
                                        />
                                      </td>
                                      <td>
                                        <input
                                          type="text"
                                          className="form-control form-control-sm"
                                          inputMode="decimal"
                                          placeholder="0.00"
                                          value={row.basicPrice || ""}
                                          onChange={(e) =>
                                            handleMaterialDecimalChange(
                                              index,
                                              "basicPrice",
                                              e.target.value,
                                            )
                                          }
                                          onClick={(e) => e.stopPropagation()}
                                        />
                                      </td>
                                      <td>
                                        <select
                                          className="form-control form-control-sm"
                                          value={row.gst || ""}
                                          onChange={(e) =>  handleGstChange(index, "gst", e.target.value)}>
                                         <option value="">Select GST</option>
                                         <option value="0">0%</option>
                                         <option value="5">5%</option>
                                         <option value="18">18%</option>
                                         <option value="28">28%</option>
                                        </select>
                                     </td>
                                      <td>
                                        <input
                                          type="text"
                                          className="form-control form-control-sm"
                                          value={
                                            row.gst !== ""
                                              ? formatMaterialAmount(
                                                  rowPricing.gstValue,
                                                )
                                              : ""
                                          }
                                          readOnly
                                          style={{ backgroundColor: "#f8f9fa" }}
                                          onClick={(e) => e.stopPropagation()}
                                        />
                                      </td>
                                      <td>
                                        <input
                                          type="text"
                                          className="form-control form-control-sm"
                                          value={row.totalValue || ""}
                                          readOnly
                                          style={{ backgroundColor: "#f8f9fa" }}
                                          onClick={(e) => e.stopPropagation()}
                                        />
                                      </td>
                                      {/* <td>
                                    <input
                                      type="text"
                                      className="form-control form-control-sm"
                                      inputMode="decimal"
                                      placeholder="0.00"
                                      value={row.quotePrice || ''}
                                      onChange={(e) =>
                                        handleMaterialDecimalChange(
                                          index,
                                          'quotePrice',
                                          e.target.value
                                        )
                                      }
                                      onClick={(e) => e.stopPropagation()}
                                    />
                                  </td> */}
                                      <td
                                        style={{
                                          verticalAlign: "top",
                                          minWidth: "180px",
                                        }}
                                      >
                                        <textarea
                                          className="form-control form-control-sm"
                                          rows={2}
                                          value={row.technicalRemarks || ""}
                                          onChange={(e) =>
                                            updateMaterial(
                                              index,
                                              "technicalRemarks",
                                              e.target.value,
                                            )
                                          }
                                          onClick={(e) => e.stopPropagation()}
                                          placeholder="Technical remarks"
                                          style={{
                                            resize: "vertical",
                                            minWidth: "160px",
                                            whiteSpace: "pre-wrap",
                                            wordBreak: "break-word",
                                          }}
                                        />
                                      </td>
                                      <td
                                        className="text-center"
                                        style={{ whiteSpace: "nowrap" }}
                                        onClick={(e) => e.stopPropagation()}
                                      >
                                        <div className="btn-group" role="group">
                                          <button
                                            type="button"
                                            className="btn btn-sm btn-success"
                                            onClick={() =>
                                              addMaterialRowAfter(index)
                                            }
                                            title="Add row"
                                            style={{ padding: "3px 8px" }}
                                          >
                                            <i
                                              className="fa-solid fa-plus"
                                              aria-hidden="true"
                                            />
                                          </button>
                                          <button
                                            type="button"
                                            className="btn btn-sm btn-danger"
                                            onClick={() =>
                                              removeMaterialRow(index)
                                            }
                                            disabled={materials.length <= 1}
                                            title="Delete row"
                                            style={{ padding: "3px 8px" }}
                                          >
                                            <i
                                              className="fa-solid fa-trash-can"
                                              aria-hidden="true"
                                            />
                                          </button>
                                        </div>
                                      </td>
                                    </tr>
                                  );
                                })}
                                {getFilledMaterials().length > 0 && (
                                  <tr style={{ backgroundColor: "#f4f6f9" }}>
                                    <td
                                      colSpan={7}
                                      className="text-right align-middle"
                                      style={{
                                        fontSize: "13px",
                                        fontWeight: 600,
                                      }}
                                    >
                                      Gross Total
                                      {quoteDetails.quoteCurrency
                                        ? ` (${quoteDetails.quoteCurrency})`
                                        : ""}
                                    </td>
                                    <td
                                      className="align-middle"
                                      style={{
                                        fontSize: "13px",
                                        fontWeight: 600,
                                      }}
                                    >
                                      {formatMaterialAmount(
                                        materialGrossTotals.grossTotalBasicPrice,
                                      )}
                                    </td>
                                    <td />
                                    <td
                                      className="align-middle"
                                      style={{
                                        fontSize: "13px",
                                        fontWeight: 600,
                                      }}
                                    >
                                      {formatMaterialAmount(
                                        materialGrossTotals.grossGstValue,
                                      )}
                                    </td>
                                    <td
                                      className="align-middle"
                                      style={{
                                        fontSize: "13px",
                                        fontWeight: 600,
                                      }}
                                    >
                                      {formatMaterialAmount(
                                        materialGrossTotals.grossTotalValue,
                                      )}
                                    </td>
                                    <td colSpan={2} />
                                  </tr>
                                )}
                              </tbody>
                            </table>
                          </div>
                          {materials.map((row, index) => {
                            if (!showMaterialCodeDropdown[index]) return null;
                            const pos = materialCodeDropdownPos[index];
                            if (!pos) return null;

                            const filtered = getFilteredMaterialCatalog(index);

                            return createPortal(
                              <div
                                key={`material-code-dd-${index}`}
                                style={{
                                  position: "fixed",
                                  top: pos.top,
                                  left: pos.left,
                                  width: pos.width,
                                  maxHeight: "240px",
                                  overflowY: "auto",
                                  backgroundColor: "#fff",
                                  border: "1px solid #ced4da",
                                  borderRadius: "4px",
                                  zIndex: 20000,
                                  boxShadow: "0 4px 10px rgba(0,0,0,0.15)",
                                }}
                              >
                                {filtered.length > 0
                                  ? filtered.map((material, mi) => (
                                      <div
                                        key={material._id}
                                        onMouseDown={(e) => {
                                          e.preventDefault();
                                          handleMaterialCodeSelect(
                                            index,
                                            material,
                                          );
                                        }}
                                        onMouseEnter={() =>
                                          setSelectedMaterialCodeIndex(
                                            (prev) => ({
                                              ...prev,
                                              [index]: mi,
                                            }),
                                          )
                                        }
                                        style={{
                                          padding: "8px 12px",
                                          cursor: "pointer",
                                          borderBottom: "1px solid #f0f0f0",
                                          backgroundColor:
                                            mi ===
                                            (selectedMaterialCodeIndex[index] ??
                                              -1)
                                              ? "#e9ecef"
                                              : "#fff",
                                        }}
                                      >
                                        <strong style={{ fontSize: "13px" }}>
                                          {material.materialCode}
                                        </strong>
                                      </div>
                                    ))
                                  : materialCodeSearchTerms[index] && (
                                      <div
                                        style={{
                                          padding: "8px 12px",
                                          color: "#6c757d",
                                          fontSize: "13px",
                                        }}
                                      >
                                        No materials found
                                      </div>
                                    )}
                              </div>,
                              document.body,
                            );
                          })}

                          {materials.map((row, index) => {
                              if (!showMaterialDescDropdown[index]) return null;
                            const pos = materialDescDropdownPos[index];
                            if (!pos) return null;

                            const filtered =
                              getFilteredMaterialDescCatalog(index);

                            return createPortal(
                              <div
                                key={`material-desc-dd-${index}`}
                                style={{
                                  position: "fixed",
                                  top: pos.top,
                                  left: pos.left,
                                  width: pos.width,
                                  maxHeight: "250px",
                                  overflowY: "auto",
                                  backgroundColor: "#fff",
                                  border: "1px solid #ced4da",
                                  borderRadius: "4px",
                                  zIndex: 20000,
                                  boxShadow: "0 4px 10px rgba(0,0,0,0.15)",
                                }}
                              >
                                {filtered.length > 0 ? (
                                  filtered.map((material, mi) => (
                                    <div
                                      key={material._id}
                                      onMouseDown={(e) => {
                                        e.preventDefault();
                                        handleMaterialDescSelect(
                                          index,
                                          material,
                                        );
                                      }}
                                      onMouseEnter={() =>
                                        setSelectedMaterialDescIndex(
                                          (prev) => ({ ...prev, [index]: mi }),
                                        )
                                      }
                                      style={{
                                        padding: "8px 12px",
                                        cursor: "pointer",
                                        borderBottom: "1px solid #f0f0f0",
                                        backgroundColor:
                                          mi ===
                                          (selectedMaterialDescIndex[index] ??
                                            -1)
                                            ? "#e9ecef"
                                            : "#fff",
                                      }}
                                    >
                                      <strong style={{ fontSize: "13px" }}>
                                        {material.productName}
                                      </strong>
                                    </div>
                                  ))
                                ) : (
                                  <div
                                    style={{
                                      padding: "8px 12px",
                                      color: "#6c757d",
                                      fontSize: "13px",
                                    }}
                                  >
                                    No materials found
                                  </div>
                                )}
                              </div>,
                              document.body,
                            );
                          })}
                      </div>
                    )}

                    {activeTab === 'attachments' && (
                      <div>
                        <div className="row mb-3">
                          <div className="col-md-8">
                            <p className="text-muted mb-2" style={{ fontSize: '13px' }}>
                              Files are stored securely and can be downloaded later.
                            </p>
                            <input
                              ref={attachmentInputRef}
                              type="file"
                              multiple
                              style={{ display: 'none' }}
                              onChange={handleAttachmentFileSelect}
                              disabled={isLoading || isUploadingAttachments}
                            />
                            <button
                              type="button"
                              className="btn btn-blue"
                              onClick={() => attachmentInputRef.current?.click()}
                              disabled={
                                isLoading ||
                                isUploadingAttachments ||
                                sqOwnerAttachments.length + pendingAttachmentFiles.length >=
                                  MAX_SQ_OWNER_ATTACHMENTS
                              }
                            >
                              <i className="fa-solid fa-paperclip mr-1" />
                              Choose Files
                            </button>
                            <span className="text-muted ml-2" style={{ fontSize: '12px' }}>
                              {sqOwnerAttachments.length + pendingAttachmentFiles.length} /{' '}
                              {MAX_SQ_OWNER_ATTACHMENTS} files
                            </span>
                          </div>
                        </div>

                        {isUploadingAttachments && (
                          <div className="mb-3 text-muted" style={{ fontSize: '13px' }}>
                            <span className="mr-2">
                              <PulseLoader color="#1199B2" size={6} />
                            </span>
                            Uploading attachments...
                          </div>
                        )}

                        {sqOwnerAttachments.length > 0 && (
                          <div className="mb-4">
                            <div
                              className="fwt_600 mb-2"
                              style={{ fontSize: '14px', color: '#003366' }}
                            >
                              Uploaded attachments
                            </div>
                            <div
                              style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '8px'
                              }}
                            >
                              {sqOwnerAttachments.map((att) => (
                                <div
                                  key={att.s3Key}
                                  className="d-flex justify-content-between align-items-center p-2"
                                  style={{
                                    border: '1px solid #dee2e6',
                                    borderRadius: '6px',
                                    background: '#f8f9fa',
                                    gap: '10px'
                                  }}
                                >
                                  <div style={{ flex: 1, minWidth: 0 }}>
                                    <div
                                      style={{
                                        fontSize: '13px',
                                        fontWeight: 600,
                                        wordBreak: 'break-word'
                                      }}
                                    >
                                      <i className="fa-solid fa-file mr-1 text-secondary" />
                                      {att.originalFileName || att.s3Key}
                                    </div>
                                    {att.uploadedAt && (
                                      <div className="text-muted" style={{ fontSize: '11px' }}>
                                        Uploaded{' '}
                                        {new Date(att.uploadedAt).toLocaleString()}
                                      </div>
                                    )}
                                  </div>
                                  <div className="d-flex" style={{ gap: '8px', flexShrink: 0 }}>
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-outline-primary"
                                      onClick={() => downloadSqOwnerAttachment(att.s3Key)}
                                      disabled={isUploadingAttachments}
                                    >
                                      <i className="fa-solid fa-download mr-1" />
                                      Download
                                    </button>
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-outline-danger"
                                      onClick={() => removeSqOwnerAttachment(att.s3Key)}
                                      disabled={isUploadingAttachments}
                                    >
                                      <i className="fa-solid fa-trash-can" />
                                    </button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {pendingAttachmentFiles.length > 0 && (
                          <div>
                            <div
                              className="fwt_600 mb-2"
                              style={{ fontSize: '14px', color: '#003366' }}
                            >
                              Pending upload (saved when you click Next)
                            </div>
                            <div
                              style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '8px'
                              }}
                            >
                              {pendingAttachmentFiles.map((file, idx) => (
                                <div
                                  key={`${file.name}-${idx}`}
                                  className="d-flex justify-content-between align-items-center p-2"
                                  style={{
                                    border: '1px dashed #cbd5e1',
                                    borderRadius: '6px',
                                    background: '#fbfdff',
                                    gap: '10px'
                                  }}
                                >
                                  <div style={{ flex: 1, minWidth: 0 }}>
                                    <div
                                      style={{
                                        fontSize: '13px',
                                        wordBreak: 'break-word'
                                      }}
                                    >
                                      {file.name}
                                    </div>
                                    <div className="text-muted" style={{ fontSize: '11px' }}>
                                      {formatFileSize(file.size)}
                                      {file.type ? ` · ${file.type}` : ''}
                                    </div>
                                  </div>
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-outline-danger"
                                    onClick={() => removePendingAttachment(idx)}
                                    disabled={isUploadingAttachments}
                                  >
                                    Remove
                                  </button>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {sqOwnerAttachments.length === 0 &&
                          pendingAttachmentFiles.length === 0 && (
                            <div
                              className="text-center text-muted py-4"
                              style={{
                                border: '1px dashed #dee2e6',
                                borderRadius: '6px',
                                fontSize: '13px'
                              }}
                            >
                              <i
                                className="fa-solid fa-cloud-arrow-up mb-2"
                                style={{ fontSize: '24px', display: 'block' }}
                              />
                              No attachments yet. Click &quot;Choose Files&quot; to add documents.
                            </div>
                          )}
                      </div>
                    )}

                    {activeTab === 'customer' && (
                      <div>
                        <div className="row align-items-end mb-3">
                          <div className="col-md-6">
                            <div className="form-group mb-0">
                              <label>Search Customer</label>
                              <div className="input-group">
                                <input
                                  type="text"
                                  className="form-control"
                                  placeholder="Search by customer name or email..."
                                  value={customerSearch}
                                  onChange={handleCustomerSearchChange}
                                  onKeyPress={handleCustomerSearchKeyPress}
                                />
                                <div className="input-group-append">
                                  <button
                                    type="button"
                                    className="btn btn-blue"
                                    onClick={handleCustomerSearch}
                                    disabled={customerSearchLoading}
                                  >
                                    {customerSearchLoading ? (
                                      <PulseLoader color="#fff" size={6} />
                                    ) : (
                                      <i className="fa-solid fa-search" />
                                    )}
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        {customerSearchResults.length > 0 && (
                          <div
                            className="mb-3"
                            style={{
                              border: '1px solid #dee2e6',
                              borderRadius: '6px',
                              overflow: 'hidden'
                            }}
                          >
                            <div
                              style={{
                                padding: '8px 12px',
                                backgroundColor: '#f8f9fa',
                                fontWeight: 600,
                                fontSize: '13px'
                              }}
                            >
                              Customer search results —{' '}
                              {
                                customerSearchResults.filter(
                                  (row) => !isCustomerAlreadyAdded(row.email)
                                ).length
                              }{' '}
                              to add
                            </div>
                            <div style={{ maxHeight: '220px', overflowY: 'auto' }}>
                              <table className="table table-sm table-bordered mb-0">
                                <thead>
                                  <tr style={{ backgroundColor: '#003366', color: '#fff' }}>
                                    <th style={{ fontSize: '11px' }}>Customer Name</th>
                                    <th style={{ fontSize: '11px' }}>Email</th>
                                    <th style={{ fontSize: '11px' }}>Contact Person</th>
                                    <th style={{ fontSize: '11px' }}>Designation</th>
                                    <th style={{ fontSize: '11px' }}>Phone</th>
                                    <th style={{ fontSize: '11px', width: '70px' }} />
                                  </tr>
                                </thead>
                                <tbody>
                                  {customerSearchResults.map((row, idx) => {
                                    const email = row.email || ''
                                    const alreadyAdded = isCustomerAlreadyAdded(email)
                                    return (
                                      <tr
                                        key={`${row.customerId}-${row.contactId || idx}`}
                                        style={
                                          alreadyAdded ? { backgroundColor: '#f0f0f0' } : undefined
                                        }
                                      >
                                        <td style={{ fontSize: '12px' }}>
                                          {row.customerName || '-'}
                                        </td>
                                        <td style={{ fontSize: '12px' }}>{email || '-'}</td>
                                        <td style={{ fontSize: '12px' }}>
                                          {row.contactPerson || '-'}
                                        </td>
                                        <td style={{ fontSize: '12px' }}>
                                          {row.designation || '-'}
                                        </td>
                                        <td style={{ fontSize: '12px' }}>
                                          {row.contactNumber || '-'}
                                        </td>
                                        <td className="text-center">
                                          <button
                                            type="button"
                                            className={`btn btn-sm ${
                                              alreadyAdded ? 'btn-secondary' : 'btn-blue'
                                            }`}
                                            onClick={() => selectCustomer(row)}
                                            disabled={!email?.trim() || alreadyAdded}
                                          >
                                            {alreadyAdded ? 'Added' : 'Select'}
                                          </button>
                                        </td>
                                      </tr>
                                    )
                                  })}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}

                        <div style={{ overflowX: 'auto' }}>
                          <table className="table table-bordered table-hover mb-0">
                            <thead>
                              <tr style={{ backgroundColor: '#003366', color: '#fff' }}>
                                <th style={{ fontSize: '12px' }}>S.No</th>
                                <th style={{ fontSize: '12px' }}>Customer Name</th>
                                <th style={{ fontSize: '12px' }}>Customer Mail ID</th>
                                <th style={{ fontSize: '12px' }}>Phone Number</th>
                                <th style={{ fontSize: '12px', width: '90px' }}>Action</th>
                              </tr>
                            </thead>
                            <tbody>
                              {customers.map((row, index) => (
                                <tr key={index}>
                                  <td>{index + 1}</td>
                                  <td>
                                    <input
                                      type="text"
                                      className="form-control form-control-sm"
                                      value={row.customerName}
                                      onChange={(e) =>
                                        updateCustomer(index, 'customerName', e.target.value)
                                      }
                                    />
                                  </td>
                                  <td style={{ position: 'relative', minWidth: '220px' }}>
                                    <input
                                      type="text"
                                      className="form-control form-control-sm"
                                      value={row.customerMailId}
                                      placeholder="Type 3+ letters (name or email)"
                                      onChange={(e) =>
                                        handleCustomerMailIdChange(index, e.target.value)
                                      }
                                      onBlur={handleCustomerMailIdBlur}
                                      autoComplete="off"
                                    />
                                    {customerEmailSuggestIndex === index &&
                                      (isSearchingCustomerEmails ||
                                        customerEmailSuggestions.length > 0) && (
                                        <div
                                          style={{
                                            position: 'absolute',
                                            top: '100%',
                                            left: 0,
                                            right: 0,
                                            zIndex: 1060,
                                            backgroundColor: '#fff',
                                            border: '1px solid #d8e0ea',
                                            borderRadius: '8px',
                                            boxShadow: '0 10px 24px rgba(15, 23, 42, 0.14)',
                                            maxHeight: '220px',
                                            overflowY: 'auto',
                                            marginTop: '2px'
                                          }}
                                        >
                                          {isSearchingCustomerEmails ? (
                                            <div
                                              className="text-muted"
                                              style={{ padding: '10px 12px', fontSize: '13px' }}
                                            >
                                              <i className="fa-solid fa-spinner fa-spin mr-2" />
                                              Searching customer contacts...
                                            </div>
                                          ) : customerEmailSuggestions.length === 0 ? (
                                            <div
                                              className="text-muted"
                                              style={{ padding: '10px 12px', fontSize: '13px' }}
                                            >
                                              No matching contacts found
                                            </div>
                                          ) : (
                                            customerEmailSuggestions.map((contact, cIdx) => (
                                              <button
                                                key={
                                                  contact.contactId ||
                                                  `${contact.email}-${cIdx}`
                                                }
                                                type="button"
                                                className="dropdown-item"
                                                style={{
                                                  display: 'block',
                                                  width: '100%',
                                                  textAlign: 'left',
                                                  padding: '8px 12px',
                                                  whiteSpace: 'normal',
                                                  cursor: 'pointer'
                                                }}
                                                onMouseDown={(e) => {
                                                  e.preventDefault()
                                                  handleSelectCustomerEmail(index, contact)
                                                }}
                                              >
                                                <span
                                                  className="d-block fwt_600"
                                                  style={{ fontSize: '13px' }}
                                                >
                                                  {contact.contactPerson ||
                                                    contact.customerName ||
                                                    contact.email}
                                                </span>
                                                <small className="text-muted d-block">
                                                  {contact.email}
                                                </small>
                                                {contact.customerName && (
                                                  <small className="text-muted">
                                                    {contact.customerName}
                                                  </small>
                                                )}
                                              </button>
                                            ))
                                          )}
                                        </div>
                                      )}
                                  </td>
                                  <td>
                                    <input
                                      type="text"
                                      className="form-control form-control-sm"
                                      value={row.phoneNumber || ''}
                                      onChange={(e) =>
                                        updateCustomer(
                                          index,
                                          'phoneNumber',
                                          normalizePhoneNumber(e.target.value)
                                        )
                                      }
                                      onPaste={(e) => {
                                        e.preventDefault()
                                        const pasted = e.clipboardData.getData('text')
                                        updateCustomer(
                                          index,
                                          'phoneNumber',
                                          normalizePhoneNumber(pasted)
                                        )
                                      }}
                                      maxLength={10}
                                      inputMode="numeric"
                                    />
                                  </td>
                                  <td className="text-center" style={{ whiteSpace: 'nowrap' }}>
                                    <div className="btn-group" role="group">
                                      <button
                                        type="button"
                                        className="btn btn-sm btn-success"
                                        onClick={() => addCustomerRowAfter(index)}
                                        style={{ padding: '3px 8px' }}
                                      >
                                        <i className="fa-solid fa-plus" aria-hidden="true" />
                                      </button>
                                      <button
                                        type="button"
                                        className="btn btn-sm btn-danger"
                                        onClick={() => removeCustomerRow(index)}
                                        disabled={customers.length <= 1}
                                        style={{ padding: '3px 8px' }}
                                      >
                                        <i className="fa-solid fa-trash-can" aria-hidden="true" />
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {activeTab === 'preview' && canAccessPreview && (
                      <div>
                        <div
                          className="mb-4"
                          style={{
                            border: '1px solid #dee2e6',
                            borderRadius: '6px',
                            overflow: 'hidden'
                          }}
                        >
                          <div
                            style={{
                              padding: '10px 14px',
                              backgroundColor: '#f8f9fa',
                              fontWeight: 600,
                              fontSize: '14px'
                            }}
                          >
                            <i className="fa-solid fa-clipboard-check mr-2 text-primary" />
                            Review Sales Quote
                          </div>
                          <div className="p-3">
                            <div className="row mb-2">
                              <div className="col-md-3">
                                <strong>Quotation Number
                                :</strong> {quoteDetails.quoteNumber || '-'}
                              </div>
                              <div className="col-md-3">
                                <strong>Quotation Date:</strong>{' '}
                                {quoteDetails.quoteDate
                                  ? formatDateForApi(quoteDetails.quoteDate)
                                  : '-'}
                              </div>
                              <div className="col-md-3">
                                <strong>Quotation Validity Date:</strong>{' '}
                                {quoteDetails.quoteDeliveryTimeDisplay ||
                                  formatDeliveryDateTimePreview(
                                    quoteDetails.quoteDeliveryDate,
                                    getCurrentTimeHHmm()
                                  )}
                              </div>
                              <div className="col-md-3">
                                <strong>Currency:</strong> {quoteDetails.quoteCurrency || '-'}
                              </div>
                            </div>
                            <div className="mb-3">
                              <strong>Quotation Summary:</strong> {quoteDetails.quoteSummary || '-'}
                            </div>
                            <div className="table-responsive mb-3">
                              <table className="table table-sm table-bordered mb-0">
                                <thead>
                                  <tr style={{ backgroundColor: '#003366', color: '#fff' }}>
                                    <th style={{ fontSize: '11px' }}>Material Code</th>
                                    <th style={{ fontSize: '11px' }}>Description</th>
                                    <th style={{ fontSize: '11px' }}>PR Number</th>
                                    <th style={{ fontSize: '11px' }}>HSN</th>
                                    <th style={{ fontSize: '11px' }}>UOM</th>
                                    <th style={{ fontSize: '11px' }}>Qty</th>
                                    <th style={{ fontSize: '11px' }}>Basic Price</th>
                                    <th style={{ fontSize: '11px' }}>GST %</th>
                                    <th style={{ fontSize: '11px' }}>GST Value</th>
                                    <th style={{ fontSize: '11px' }}>Total Value</th>
                                    {/* <th style={{ fontSize: '11px' }}>Quote Price</th> */}
                                    <th style={{ fontSize: '11px' }}>Technical Remarks</th>
                                    {/* <th style={{ fontSize: '11px' }}>Attachments</th> */}
                                  </tr>
                                </thead>
                                <tbody>
                                  {getFilledMaterials().map((row, index) => {
                                    const rowPricing = calcMaterialPricing(
                                      row.basicPrice,
                                      row.quantity,
                                      row.gst
                                    )
                                    return (
                                    <tr key={index}>
                                      <td>{row.materialCode || '-'}</td>
                                      <td
                                        style={{
                                          whiteSpace: 'normal',
                                          wordBreak: 'break-word',
                                          maxWidth: '280px'
                                        }}
                                      >
                                        {row.materialDescription || '-'}
                                      </td>
                                      <td>{row.prNumber || '-'}</td>
                                      <td>{row.hsn || '-'}</td>
                                      <td>{row.uom || '-'}</td>
                                      <td>{row.quantity || '-'}</td>
                                      <td>{row.basicPrice || '-'}</td>
                                      <td>{row.gst || '-'}</td>
                                      <td>
                                        {rowPricing.gstValue > 0
                                          ? formatMaterialAmount(rowPricing.gstValue)
                                          : '-'}
                                      </td>
                                      <td>{row.totalValue || '-'}</td>
                                      {/* <td>{row.quotePrice || '-'}</td> */}
                                      <td
                                        style={{
                                          whiteSpace: 'pre-wrap',
                                          wordBreak: 'break-word',
                                          maxWidth: '180px'
                                        }}
                                      >
                                        {row.technicalRemarks || '-'}
                                      </td>
                                      {/* <td style={{ fontSize: '11px' }}>
                                        {Array.isArray(row.attachments) && row.attachments.length > 0
                                          ? row.attachments
                                              .map((a) => a.originalFileName || a.s3Key)
                                              .filter(Boolean)
                                              .join(', ')
                                          : '-'}
                                      </td> */}
                                    </tr>
                                    )
                                  })}
                                  {getFilledMaterials().length > 0 && (
                                    <tr style={{ backgroundColor: '#f4f6f9' }}>
                                      <td
                                        colSpan={6}
                                        className="text-right align-middle"
                                        style={{ fontSize: '12px', fontWeight: 600 }}
                                      >
                                        Gross Total
                                        {quoteDetails.quoteCurrency
                                          ? ` (${quoteDetails.quoteCurrency})`
                                          : ''}
                                      </td>
                                      <td
                                        className="align-middle"
                                        style={{ fontSize: '12px', fontWeight: 600 }}
                                      >
                                        {formatMaterialAmount(
                                          materialGrossTotals.grossTotalBasicPrice
                                        )}
                                      </td>
                                      <td />
                                      <td
                                        className="align-middle"
                                        style={{ fontSize: '12px', fontWeight: 600 }}
                                      >
                                        {formatMaterialAmount(materialGrossTotals.grossGstValue)}
                                      </td>
                                      <td
                                        className="align-middle"
                                        style={{ fontSize: '12px', fontWeight: 600 }}
                                      >
                                        {formatMaterialAmount(materialGrossTotals.grossTotalValue)}
                                      </td>
                                      <td />
                                    </tr>
                                  )}
                                </tbody>
                              </table>
                            </div>
                            {sqOwnerAttachments.length > 0 && (
                              <div className="mb-3">
                                <strong>Attachments:</strong>
                                <ul className="mb-0 mt-1" style={{ fontSize: '13px' }}>
                                  {sqOwnerAttachments.map((att) => (
                                    <li key={att.s3Key}>
                                      <button
                                        type="button"
                                        className="btn btn-link btn-sm p-0 align-baseline"
                                        onClick={() => downloadSqOwnerAttachment(att.s3Key)}
                                      >
                                        {att.originalFileName || att.s3Key}
                                      </button>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                            <div className="table-responsive">
                              <table className="table table-sm table-bordered mb-0">
                                <thead>
                                  <tr style={{ backgroundColor: '#003366', color: '#fff' }}>
                                    <th style={{ fontSize: '11px' }}>Customer Name</th>
                                    <th style={{ fontSize: '11px' }}>Customer Mail ID</th>
                                    <th style={{ fontSize: '11px' }}>Phone Number</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {getFilledCustomers().map((row, index) => (
                                    <tr key={index}>
                                      <td>{row.customerName || '-'}</td>
                                      <td>{row.customerMailId || '-'}</td>
                                      <td>{row.phoneNumber || '-'}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </div>

                        <div
                          className="mb-4"
                          style={{
                            border: '1px solid #dee2e6',
                            borderRadius: '6px',
                            overflow: 'hidden'
                          }}
                        >
                          <div
                            style={{
                              padding: '10px 14px',
                              backgroundColor: '#f8f9fa',
                              fontWeight: 600,
                              fontSize: '14px'
                            }}
                          >
                            <i className="fa-solid fa-file-pdf mr-2 text-primary" />
                            Preview PDF
                          </div>
                          <div className="p-3">
                            {quoteDetails.pdfLink ? (
                              <>
                                <div className="mb-3">
                                  {pdfPreviewUrl ? (
                                    <a
                                      href={pdfPreviewUrl}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="btn btn-sm btn-outline-primary"
                                    >
                                      <i className="fa-solid fa-up-right-from-square mr-1" />
                                      Open PDF in new tab
                                    </a>
                                  ) : (
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-outline-primary"
                                      disabled={isLoadingPdfPreview}
                                    >
                                      {isLoadingPdfPreview ? (
                                        <>
                                          <i className="fa-solid fa-spinner fa-spin mr-1" />
                                          Loading PDF...
                                        </>
                                      ) : (
                                        'PDF unavailable'
                                      )}
                                    </button>
                                  )}
                                </div>
                                <div
                                  style={{
                                    border: '1px solid #dee2e6',
                                    borderRadius: '4px',
                                    overflow: 'hidden',
                                    minHeight: '420px'
                                  }}
                                >
                                  {isLoadingPdfPreview ? (
                                    <div className="text-center py-5 text-muted">
                                      <PulseLoader color="#1199B2" size={8} />
                                      <div className="mt-2">Loading PDF preview...</div>
                                    </div>
                                  ) : pdfPreviewUrl ? (
                                    <iframe
                                      title="Sales Quote PDF Preview"
                                      src={pdfPreviewUrl}
                                      style={{ width: '100%', height: '420px', border: 'none' }}
                                    />
                                  ) : (
                                    <p className="text-muted mb-0 p-3">
                                      Could not load PDF preview. Try again shortly.
                                    </p>
                                  )}
                                </div>
                              </>
                            ) : (
                              <p className="text-muted mb-0">
                                PDF is being generated. It will appear here once available.
                              </p>
                            )}
                          </div>
                        </div>

                        <div
                          style={{
                            border: '1px solid #dee2e6',
                            borderRadius: '6px',
                            overflow: 'hidden'
                          }}
                        >
                          <div
                            style={{
                              padding: '10px 14px',
                              backgroundColor: '#f8f9fa',
                              fontWeight: 600,
                              fontSize: '14px'
                            }}
                          >
                            <i className="fa-solid fa-envelope mr-2 text-primary" />
                            Send Sales Quote Email
                          </div>
                          <div className="p-3">
                            <div className="form-group mb-2">
                              <label>To Emails (new customers)</label>
                              <div className="txtdsply" style={{ fontSize: '13px' }}>
                                {getPendingCustomers()
                                  .map((c) => c.customerMailId.trim())
                                  .join(', ') || '—'}
                              </div>
                            </div>
                            {userEmail ? (
                              <div className="form-group mb-2">
                                <label>CC Emails (your email)</label>
                                <div className="txtdsply" style={{ fontSize: '13px' }}>
                                  {userEmail}
                                </div>
                              </div>
                            ) : (
                              <div className="form-group mb-2">
                                <label>CC Emails (your email)</label>
                                <div className="txtdsply text-muted" style={{ fontSize: '13px' }}>
                                  Profile email not available — add CC addresses below
                                </div>
                              </div>
                            )}
                            <div className="form-group mb-2">
                              <label>Additional CC Emails</label>
                              <div style={{ position: 'relative' }}>
                              <input
                                type="text"
                                className="form-control form-control-sm"
                                placeholder="e.g. manager@example.com, cc@company.com"
                                value={additionalCcEmails}
                                onChange={handleAdditionalCcChange}
                                onBlur={handleAdditionalCcBlur}
                                disabled={isSendingEmail}
                                autoComplete="off"
                              />
                              {(isSearchingCcUsers || ccUserSuggestions.length > 0) && (
                                <div
                                  style={{
                                    position: 'absolute',
                                    top: '100%',
                                    left: 0,
                                    right: 0,
                                    zIndex: 1060,
                                    backgroundColor: '#fff',
                                    border: '1px solid #d8e0ea',
                                    borderRadius: '8px',
                                    boxShadow: '0 10px 24px rgba(15, 23, 42, 0.14)',
                                    maxHeight: '210px',
                                    overflowY: 'auto',
                                    marginTop: '2px'
                                  }}
                                >
                                  {isSearchingCcUsers ? (
                                    <div
                                      className="text-muted"
                                      style={{ padding: '10px 12px', fontSize: '13px' }}
                                    >
                                      <i className="fa-solid fa-spinner fa-spin mr-2" />
                                      Searching users & customer contacts...
                                    </div>
                                  ) : (
                                    (() => {
                                      const existing = new Set(
                                        buildCcEmails(userEmail, additionalCcEmails).map(
                                          (item) => item.toLowerCase()
                                        )
                                      )
                                      const visible = ccUserSuggestions.filter(
                                        (user) =>
                                          !existing.has(
                                            String(user.email).trim().toLowerCase()
                                          )
                                      )
                                      if (visible.length === 0) {
                                        return (
                                          <div
                                            className="text-muted"
                                            style={{ padding: '10px 12px', fontSize: '13px' }}
                                          >
                                            No matching users or customer contacts found
                                          </div>
                                        )
                                      }
                                      return visible.map((item) => (
                                        <button
                                          key={item.email}
                                          type="button"
                                          className="dropdown-item"
                                          style={{
                                            display: 'block',
                                            width: '100%',
                                            textAlign: 'left',
                                            padding: '8px 12px',
                                            whiteSpace: 'normal',
                                            cursor: 'pointer'
                                          }}
                                          onMouseDown={(e) => {
                                            e.preventDefault()
                                            handleSelectCcUser(item.email)
                                          }}
                                        >
                                          <span
                                            className="d-block fwt_600"
                                            style={{ fontSize: '13px' }}
                                          >
                                            {item.name || item.email}
                                          </span>
                                          <small className="text-muted d-block">
                                            {item.email}
                                          </small>
                                          {item.sourceLabel && (
                                            <small className="text-muted">
                                              {item.sourceLabel}
                                            </small>
                                          )}
                                        </button>
                                      ))
                                    })()
                                  )}
                                </div>
                              )}
                              </div>
                              <small className="form-text text-muted">
                                Comma-separated. Sent as CC along with your email.
                              </small>
                              {buildCcEmails(userEmail, additionalCcEmails).length > 0 && (
                                <small className="form-text text-muted d-block">
                                  Will CC:{' '}
                                  {buildCcEmails(userEmail, additionalCcEmails).join(', ')}
                                </small>
                              )}
                            </div>
                            <div className="form-group mb-3">
                              <label>Already emailed (skipped)</label>
                              <div className="txtdsply text-muted" style={{ fontSize: '13px' }}>
                                {getAlreadyEmailedCustomers()
                                  .map((c) => c.customerMailId.trim())
                                  .join(', ') || '—'}
                              </div>
                            </div>
                            {!isReadyToSendEmail && (
                              <div className="alert alert-warning py-2 mb-3" role="alert">
                                Complete all quote details, materials, customers, wait for PDF,
                                and activate the quote before sending email.
                              </div>
                            )}
                            {isReadyToSendEmail && !hasPendingCustomersToEmail() && (
                              <div className="alert alert-success py-2 mb-3" role="alert">
                                <i className="fa-solid fa-circle-check mr-1" />
                                All customers have already been emailed.
                              </div>
                            )}
                            {isReadyToSendEmail && hasPendingCustomersToEmail() && isMailSent && (
                              <div className="alert alert-info py-2 mb-3" role="alert">
                                <i className="fa-solid fa-circle-info mr-1" />
                                New customer(s) added — click Send Email to notify only them.
                              </div>
                            )}
                            <button
                              type="button"
                              className="btn btn-blue"
                              onClick={sendSalesQuoteEmail}
                              disabled={
                                isSendingEmail ||
                                !isReadyToSendEmail ||
                                !hasPendingCustomersToEmail()
                              }
                            >
                              {isSendingEmail ? 'Sending...' : 'Send Email'}
                            </button>
                          </div>
                        </div>

                        {isQuoteActive && (
                          <div className="text-center">
                            <Link to="/salesQuotes" className="btn btn-blue">
                              Back to Sales Quotes List
                            </Link>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  )}

                  <div className="card-footer d-flex justify-content-center align-items-center">
                    {!isLoadingQuote && activeTab === 'details' && (
                      <button
                        type="button"
                        className="btn btn-blue"
                        onClick={() => goToTab('materials')}
                        disabled={isLoading}
                      >
                        Next
                      </button>
                    )}

                    {!isLoadingQuote && activeTab === 'materials' && (
                      <>
                        <button
                          type="button"
                          className="btn btn-blue mr-2"
                          onClick={() => goToTab('details')}
                          disabled={isLoading}
                        >
                          Previous
                        </button>
                        <button
                          type="button"
                          className="btn btn-blue"
                          onClick={() => goToTab('attachments')}
                          disabled={isLoading}
                        >
                          Next
                        </button>
                      </>
                    )}

                    {!isLoadingQuote && activeTab === 'attachments' && (
                      <>
                        <button
                          type="button"
                          className="btn btn-blue mr-2"
                          onClick={() => goToTab('materials')}
                          disabled={isLoading || isUploadingAttachments}
                        >
                          Previous
                        </button>
                        <button
                          type="button"
                          className="btn btn-blue"
                          onClick={() => goToTab('customer')}
                          disabled={isLoading || isUploadingAttachments}
                        >
                          {isUploadingAttachments ? 'Uploading...' : 'Next'}
                        </button>
                      </>
                    )}

                    {!isLoadingQuote && activeTab === 'customer' && (
                      <>
                        <button
                          type="button"
                          className="btn btn-blue mr-2"
                          onClick={() => goToTab('attachments')}
                          disabled={isLoading}
                        >
                          Previous
                        </button>
                        <button
                          type="submit"
                          className="btn btn-blue"
                          disabled={
                            isLoading ||
                            isLoadingQuote ||
                            isFetchingQuoteNumber ||
                            isUploadingAttachments ||
                            !quoteDetails.quoteNumber
                          }
                        >
                          {isLoading
                            ? 'Submitting...'
                            : isQuoteActive
                              ? 'Update Sales Quote'
                              : quoteId
                                ? 'Activate Sales Quote'
                                : 'Create & Activate Sales Quote'}
                        </button>
                      </>
                    )}

                    {!isLoadingQuote && activeTab === 'preview' && (
                      <button
                        type="button"
                        className="btn btn-blue"
                        onClick={() => goToTab('customer')}
                      >
                        Previous
                      </button>
                    )}
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default SalesQuotesAdd
